import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Input, { Textarea } from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Tabs from '../../components/ui/Tabs';
import Badge from '../../components/ui/Badge';
import MultiSelectChips from '../../components/ui/MultiSelectChips';
import { Icon } from '../../components/ui/Icon';
import SystemConstantPicker from '../../components/ui/SystemConstantPicker';
import ColumnValuePicker from '../../components/ui/ColumnValuePicker';

import { createTask, updateTask } from '../../store/slices/tasksSlice';
import { tasksApi } from '../../services/api';
import { API_BASE_URL } from '../../services/apiClient';
import { fetchChTables, fetchChColumns } from '../../store/slices/metadataSlice';
import {
  fetchSegments,
  fetchMudurlukler,
} from '../../store/slices/referencesSlice';
import { pushToast } from '../../store/slices/uiSlice';
import { METRIC_TYPES } from '../../constants/navigation';
import {
  taskWizardStepSchemas,
  validate,
  firstError,
  periodFilterStatus,
  periodFilterRule,
  periodFilterRequirement,
  withoutColumnlessRows,
  isFilterValueMissing,
} from '../../utils/validation';
import { cn } from '../../utils/cn';
import {
  filterColumnsForMetric,
  decorateColumnLabel,
  selectionIsIncompatible,
  filterValueInputType,
  isDateColumn,
} from '../../utils/metricCompat';

// Tab tanımları — Stepper yerine rastgele erişimli sekmeler (edit senaryosunda
// power-user'lar tek bir alanı düzeltmek için sırayla "İleri" tıklamak
// zorunda kalmasın). Her sekme kendi validasyon şemasına sahip
// (taskWizardStepSchemas[0|1|2]). Kaydet'e basılınca tüm şemalar
// doğrulanır; hatalı olan ilk sekmeye otomatik geçilir.
const TABS = [
  { key: 'basic', label: 'Temel Bilgi', schemaIdx: 0 },
  { key: 'source', label: 'Kaynak & Metrik', schemaIdx: 1 },
  { key: 'tiers', label: 'Puanlama', schemaIdx: 2 },
];

// Her sekme kendi validasyon hatalarını üst seviyede (kırmızı nokta) göstersin
// diye hangi alan hangi sekmeye ait — prefix eşleşmesi yaparak kontrol ederiz.
const TAB_FIELD_PREFIXES = {
  basic: [
    'name',
    'description',
    'game_period_id',
    // max_points artık Temel Bilgi'de değil — non-MANUAL için tier'dan
    // türetiliyor, MANUAL için Puanlama sekmesinde elle giriliyor.
    'cancellation_policy',
    'effective_start_date',
    'effective_end_date',
    'bonus_condition',
    'segment_ids',
    'mudurluk_kodlari',
    'is_joker',
  ],
  source: [
    'metric_type',
    'source_table',
    'metric_column',
    'formula_numerator',
    'formula_denominator',
    'filters',
    'exclude_filters',
    'numerator_filters',
    'numerator_exclude_filters',
    'denominator_filters',
    'denominator_exclude_filters',
  ],
  tiers: ['tiers', 'max_points'],
};
function tabHasError(tabKey, errors) {
  if (!errors) return false;
  const prefixes = TAB_FIELD_PREFIXES[tabKey] || [];
  return Object.keys(errors).some((k) =>
    prefixes.some((p) => k === p || k.startsWith(`${p}.`)),
  );
}

const emptyForm = {
  name: '',
  description: '',
  game_period_id: '',
  metric_type: 'COUNT',
  source_table: '',
  metric_column: '',
  formula_numerator: '',
  formula_denominator: '',
  filters: [],
  exclude_filters: [],
  ratio_multiplier: 100, // RATIO tipi için çarpan; varsayılan 100 (yüzde)
  // Çapraz tablo RATIO: pay ve payda farklı tablolarda olabilir.
  is_cross_table_ratio: false,
  formula_numerator_table: '',
  formula_denominator_table: '',
  numerator_agg_func: 'SUM',   // SUM | COUNT | AVG
  denominator_agg_func: 'SUM',
  numerator_filters: [],
  numerator_exclude_filters: [],
  denominator_filters: [],
  denominator_exclude_filters: [],
  max_points: 20,
  is_joker: false,
  cancellation_policy: 'CANCEL_REVOKE',
  // Analiz §4.2 ek alanları — sort_order backend tarafından otomatik atanır
  effective_start_date: '',
  effective_end_date: '',
  bonus_condition: null, // { column, op, value, bonus_points }
  // Görev-seviyesi hedefleme (Kıbrıs SKS için ayrı görev vb.).
  // segment_ids: UI'da "Kullanıcı Tipi (Segment)" etiketiyle gösterilir; CH
  // ``segments`` tablosundan tenant-filtered gelir. Boş = dönemden devral.
  // mudurluk_kodlari: opsiyonel; boş = dönemden devral, dolu = darlaştırıcı.
  segment_ids: [],
  mudurluk_kodlari: [],
  tiers: [{ threshold_value: 0, threshold_operator: 'GTE', points: 20, sort_order: 1 }],
  // Puanlama modu: DISCRETE (kademeli) veya LINEAR (interpolasyonlu).
  // RANK dışı tüm görevler için varsayılan LINEAR; RANK için DISCRETE.
  tier_mode: 'LINEAR',
};

const BONUS_OPS = [
  { value: 'EQ', label: '= (eşit)' },
  { value: 'GT', label: '> (büyük)' },
  { value: 'GTE', label: '≥ (büyük eşit)' },
  { value: 'LT', label: '< (küçük)' },
  { value: 'LTE', label: '≤ (küçük eşit)' },
  { value: 'IS_TRUE', label: 'doğru / dolu' },
];

/**
 * Metrik tiplerine ait detaylı açıklamalar — info ikonu tooltip'inde gösterilir.
 * Her entry: summary (kısa başlık), detail (nasıl çalışır), examples (örnek senaryolar).
 */
const METRIC_DESCRIPTIONS = {
  COUNT: {
    summary: 'Satır sayısı — filtreden geçen kayıt adedi',
    detail:
      'Seçilen tabloda filtrelere uyan satır sayısını hesaplar. ' +
      'Sonuç puan eşiklerine (kademelere) karşılaştırılır.',
    examples: [
      'Kasko adet görevi: aktif poliçe sayısı ≥ 30 → 15 puan',
      'KOBİ TSS adet: yeni düzenlenen poliçe sayısı',
      'YDS adet görevi: branş filtresi ile poliçe adedi',
    ],
  },
  SUM: {
    summary: 'Kolon toplamı — seçilen alanın toplamı',
    detail:
      'Belirtilen kolonun (genellikle prim veya sigortalı sayısı) ' +
      'filtrelenmiş toplamını hesaplar.',
    examples: [
      'Toplam prim görevi: vergi_oncesi_prim ≥ 500.000 TL → 20 puan',
      'Sigortalı sayısı görevi: sigortali_sayisi_kontak toplamı',
    ],
  },
  RATIO: {
    summary: 'Oran — pay ÷ payda × çarpan',
    detail:
      'İki kolon veya iki farklı tablo arasında oran hesaplar. ' +
      '"Oran Çarpanı" ile yüzdeye çevrilebilir (çarpan = 100). ' +
      'Pay ve payda farklı tablolardaysa Çapraz Tablo modunu aç.',
    examples: [
      'HYO: (üretim / hedef) × 100 → %80 / %90 / %100 / %110 eşikleri',
      'Yenileme oranı: (yenilenen / yenilenecek) × 100',
      'Çapraz tablo: as_uretim (pay) / as_rota_hedefler (payda)',
    ],
  },
  RANK: {
    summary: 'Sıralama — en yüksek X kullanıcıya sabit puan',
    detail:
      'Kullanıcıları seçilen metriğe göre büyükten küçüğe sıralar; ' +
      'sıradaki konuma (1., 2., 3.) göre sabit puan verir. ' +
      'Genellikle Joker görev olarak kullanılır.',
    examples: [
      'Müdürlükte teklif adedi en fazla 3 acente: 1.→15, 2.→10, 3.→5 puan',
      'KÖP Katılım Oranı: müdürlük sıralamasında üst 3',
    ],
  },
  MANUAL: {
    summary: 'Manuel — admin elle puan atar',
    detail:
      'Otomatik CH sorgusu yapılmaz. Admin toplu Excel yükleme veya ' +
      'tek tek giriş ile her kullanıcıya puan atar.',
    examples: [
      'Özel etkinlik katılım puanı',
      'Sistem dışı kriter bazlı takdir / teşvik puanı',
    ],
  },
};

/**
 * Normalise a backend TaskOut (see API_CONTRACT.md §2.3) into the flat form
 * shape used by this wizard. Backend exposes `source = { ch_table,
 * metric_column, numerator:{table,column}, denominator:{table,column}, filters,
 * rank_by }`; the UI edits flat fields for ergonomics.
 */
function fromBackendTask(row) {
  if (!row) return null;
  const source = row.source || {};
  const table =
    source.ch_table ||
    source.numerator?.table ||
    source.denominator?.table ||
    '';
  return {
    ...row,
    source_table: table,
    metric_column: source.metric_column || '',
    formula_numerator: source.numerator?.column || '',
    formula_denominator: source.denominator?.column || '',
    filters: Array.isArray(source.filters) ? source.filters : [],
    exclude_filters: Array.isArray(source.exclude_filters)
      ? source.exclude_filters
      : [],
    ratio_multiplier: source.ratio_multiplier != null ? Number(source.ratio_multiplier) : 100,
    // Çapraz tablo: num/den ayrı tablo içeriyorsa is_cross_table_ratio=true
    is_cross_table_ratio: Boolean(
      source.numerator?.table && source.denominator?.table &&
      source.numerator.table !== source.denominator.table
    ),
    formula_numerator_table: source.numerator?.table || '',
    formula_denominator_table: source.denominator?.table || '',
    numerator_agg_func: source.numerator?.agg_func || 'SUM',
    denominator_agg_func: source.denominator?.agg_func || 'SUM',
    numerator_filters: Array.isArray(source.numerator?.filters) ? source.numerator.filters : [],
    numerator_exclude_filters: Array.isArray(source.numerator?.exclude_filters) ? source.numerator.exclude_filters : [],
    denominator_filters: Array.isArray(source.denominator?.filters) ? source.denominator.filters : [],
    denominator_exclude_filters: Array.isArray(source.denominator?.exclude_filters) ? source.denominator.exclude_filters : [],
    // Hedefleme alanları — backend list/null döndürür; form çoklu seçim dizisi.
    segment_ids: Array.isArray(row.segment_ids)
      ? row.segment_ids.map((v) => String(v))
      : [],
    mudurluk_kodlari: Array.isArray(row.mudurluk_kodlari)
      ? row.mudurluk_kodlari
      : [],
    // Leave tiers as-is; backend already serialises them flat.
    tiers: Array.isArray(row.tiers) && row.tiers.length
      ? row.tiers.map((t) => ({
          threshold_operator: t.threshold_operator || 'GTE',
          threshold_value: Number(t.threshold_value ?? 0),
          points: Number(t.points ?? 0),
        }))
      : [{ threshold_value: 0, threshold_operator: 'GTE', points: 20, sort_order: 1 }],
    // bonus_condition — eski kayıtlarda column/op/value tekli alan olabilir;
    // yeni çoklu-filtre yapısına normalize et.
    bonus_condition: (() => {
      const bc = row.bonus_condition;
      if (!bc) return null;
      const bcType = (bc.type || 'SIMPLE').toUpperCase();
      if (bcType === 'CROSS_BRANCH_RATIO_THRESHOLD') {
        // Kaynak = görevin kendi metriği; sadece hedef filtrelerini normalize et.
        // Legacy: target tekli dict → target_filters listesi
        const tf = bc.target_filters?.length ? bc.target_filters
          : bc.target ? [bc.target] : [{ column: '', op: '=', value: '' }];
        return { ...bc, target_filters: tf };
      }
      // SIMPLE: legacy column/op/value → filters listesi
      const filters = bc.filters?.length ? bc.filters
        : bc.column ? [{ column: bc.column, op: bc.op || '=', value: bc.value ?? '' }]
        : [{ column: '', op: '=', value: '' }];
      return { ...bc, filters, exclude_filters: bc.exclude_filters || [] };
    })(),
    // tier_mode: mevcut kayıt varsa al, yoksa metric_type'a göre akıllı varsayılan.
    tier_mode: row.tier_mode || (row.metric_type === 'RANK' ? 'DISCRETE' : 'LINEAR'),
  };
}

/**
 * Convert the flat wizard form into the nested payload expected by
 * POST/PUT /rule-engine/tasks. Handles MANUAL (no source), RATIO
 * (numerator/denominator) and the COUNT/SUM/RANK case (metric_column).
 */
function toBackendPayload(form) {
  const {
    source_table,
    metric_column,
    formula_numerator,
    formula_denominator,
    filters,
    exclude_filters,
    segment_ids: rawSegmentIds,
    mudurluk_kodlari: rawMudurluklist,
    // Çapraz tablo alanları — backend payload'a dönüştürülür, rest'e gitmesin
    is_cross_table_ratio: _cross,
    formula_numerator_table: _numTbl,
    formula_denominator_table: _denTbl,
    numerator_agg_func: _numAgg,
    denominator_agg_func: _denAgg,
    numerator_filters: _numF,
    numerator_exclude_filters: _numEF,
    denominator_filters: _denF,
    denominator_exclude_filters: _denEF,
    // image_url upload/delete endpoint'leri aracılığıyla yönetilir;
    // görev güncelleme payload'una dahil edilmemeli (stale değer ezebilir).
    image_url: _imageUrl,
    // Read-only / server-managed alanlar — PUT payload'a dahil edilmemeli.
    // status değişikliği ayrı akışlarla (cancel, activate) yönetilir.
    id: _id,
    status: _status,
    game_period_id: _gamePeriodId,
    usage_count: _usageCount,
    last_run_avg: _lastRunAvg,
    completion_percent: _completionPercent,
    created_at: _createdAt,
    updated_at: _updatedAt,
    created_by: _createdBy,
    ...rest
  } = form;
  const segment_ids = Array.isArray(rawSegmentIds) ? rawSegmentIds : [];
  const mudurluk_list = Array.isArray(rawMudurluklist) ? rawMudurluklist : [];
  const mudurluk_kodlari = mudurluk_list.length ? mudurluk_list : null;
  const ef = withoutColumnlessRows(exclude_filters);

  let source = {};
  if (form.metric_type === 'MANUAL') {
    source = {};
  } else if (form.metric_type === 'RATIO') {
    const isCross = Boolean(form.is_cross_table_ratio);
    const numTable = (isCross ? form.formula_numerator_table : source_table) || source_table;
    const denTable = (isCross ? form.formula_denominator_table : source_table) || source_table;

    if (isCross) {
      // Çapraz tablo: her leg kendi tablo + filtre setini taşır.
      // Worker bu yapıyı okur ve ayrı CH sorguları çalıştırır.
      const numAgg = (form.numerator_agg_func || 'SUM').toUpperCase();
      const denAgg = (form.denominator_agg_func || 'SUM').toUpperCase();
      source = {
        ch_table: numTable || null,  // primary/fallback
        numerator: {
          table: numTable || null,
          agg_func: numAgg,
          // COUNT için kolon gerekmez; SUM/AVG için zorunlu
          column: numAgg !== 'COUNT' ? (formula_numerator || null) : null,
          filters: withoutColumnlessRows(form.numerator_filters),
          exclude_filters: withoutColumnlessRows(form.numerator_exclude_filters),
        },
        denominator: {
          table: denTable || null,
          agg_func: denAgg,
          column: denAgg !== 'COUNT' ? (formula_denominator || null) : null,
          filters: withoutColumnlessRows(form.denominator_filters),
          exclude_filters: withoutColumnlessRows(form.denominator_exclude_filters),
        },
        ratio_multiplier: Number(form.ratio_multiplier ?? 100),
      };
    } else {
      // Tek tablo (mevcut davranış): filtreler her iki leg'e uygulanır.
      source = {
        ch_table: source_table || null,
        numerator: source_table && formula_numerator
          ? { table: source_table, column: formula_numerator }
          : null,
        denominator: source_table && formula_denominator
          ? { table: source_table, column: formula_denominator }
          : null,
        filters: withoutColumnlessRows(filters),
        exclude_filters: ef,
        ratio_multiplier: Number(form.ratio_multiplier ?? 100),
      };
    }
  } else if (form.metric_type === 'RANK') {
    source = {
      ch_table: source_table || null,
      rank_by: metric_column || null,
      filters: withoutColumnlessRows(filters),
    };
  } else {
    // COUNT / SUM — exclude_filters burada anlamlı
    source = {
      ch_table: source_table || null,
      metric_column: metric_column || null,
      filters: withoutColumnlessRows(filters),
      exclude_filters: ef,
    };
  }

  // LINEAR modda eşikler küçükten büyüğe sıralanmalı (interpolasyon için zorunlu).
  // DISCRETE modda sıralama backend'e bırakılır.
  const tiersOut = rest.tier_mode === 'LINEAR'
    ? [...(rest.tiers || [])].sort((a, b) => Number(a.threshold_value) - Number(b.threshold_value))
    : rest.tiers;

  return {
    ...rest,
    // game_period_id: create için zorunlu (TaskCreate.TaskBase); PUT'ta TaskUpdate'de
    // olmadığından Pydantic sessizce drop eder — güvenli.
    game_period_id: _gamePeriodId,
    tiers: tiersOut,
    source,
    segment_ids,
    mudurluk_kodlari,
  };
}

export default function TaskFormWizard({
  open,
  onClose,
  mode = 'create',
  initial,
  // template: TaskTemplatePicker'den secilmisse template objesi. initial
  // zaten template.build()'in cevabi (flat form state). Baslik + info metni
  // icin saklanir.
  template = null,
}) {
  const dispatch = useDispatch();
  const periods = useSelector((s) => s.periods.items);
  const chTables = useSelector((s) => s.metadata.tables);
  const chColumns = useSelector((s) => s.metadata.columns);
  const systemParams = useSelector((s) => s.parameters.items || []);
  const {
    segments,
    segmentsStatus,
    mudurlukler,
    mudurluklerStatus,
  } = useSelector((s) => s.references);
  const [activeTab, setActiveTab] = useState('basic');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  // Metrik tipi kartlarındaki info tooltip — hangi tip açık (null = hepsi kapalı)
  // tooltipPos: butonun getBoundingClientRect() sonucu → fixed positioned render için
  const [tooltipMetric, setTooltipMetric] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  // Görsel yükleme state'leri
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageDeletePending, setImageDeletePending] = useState(false);
  const [imageSaving, setImageSaving] = useState(false);
  const [imageDragOver, setImageDragOver] = useState(false);

  useEffect(() => {
    if (open) {
      dispatch(fetchChTables());
      // Referans listeleri idle ise yükle — tenant-scoped cache slice'ta tutulur.
      if (segmentsStatus === 'idle') dispatch(fetchSegments({ include_inactive: false }));
      if (mudurluklerStatus === 'idle') dispatch(fetchMudurlukler({ only_active: true }));
      setErrors({});
      setActiveTab('basic');
      setImageFile(null);
      setImageDeletePending(false);
      if (initial) {
        // Template'ten gelen initial zaten flat form shape'i (source_table,
        // metric_column vb. dogrudan var). Backend TaskOut'tan gelen initial
        // ise source.{...} nested yapida — fromBackendTask normalize eder.
        // ``template`` prop'u varsa template branch'ine gir; yoksa backend
        // row varsayimi.
        const normalized = template
          ? initial
          : fromBackendTask(initial);
        setForm({ ...emptyForm, ...normalized });
        const rawUrl = normalized?.image_url || null;
        setImagePreview(rawUrl ? API_BASE_URL + rawUrl : null);
      } else {
        setForm(emptyForm);
        setImagePreview(null);
      }
    }
  }, [open, initial, template, dispatch, segmentsStatus, mudurluklerStatus]);

  useEffect(() => {
    if (form.source_table && !chColumns[form.source_table]) {
      dispatch(fetchChColumns(form.source_table));
    }
  }, [form.source_table, chColumns, dispatch]);

  // Çapraz tablo: pay ve payda tablolarının kolonlarını da yükle
  useEffect(() => {
    if (form.formula_numerator_table && !chColumns[form.formula_numerator_table]) {
      dispatch(fetchChColumns(form.formula_numerator_table));
    }
  }, [form.formula_numerator_table, chColumns, dispatch]);
  useEffect(() => {
    if (form.formula_denominator_table && !chColumns[form.formula_denominator_table]) {
      dispatch(fetchChColumns(form.formula_denominator_table));
    }
  }, [form.formula_denominator_table, chColumns, dispatch]);

  // Bonus koşulu tipi: SIMPLE | CROSS_BRANCH_RATIO_THRESHOLD
  const bonusType = (form.bonus_condition?.type || 'SIMPLE').toUpperCase();
  const BONUS_TYPES = [
    { value: 'SIMPLE', label: 'Basit Koşul (çoklu filtre)' },
    { value: 'CROSS_BRANCH_RATIO_THRESHOLD', label: 'Çapraz Branş Oranı (ör. DASK ≥ Konut × %50)' },
  ];
  const EMPTY_BONUS_FILTER = { column: '', op: '=', value: '' };
  const BONUS_SIMPLE_INITIAL = {
    filters: [{ ...EMPTY_BONUS_FILTER }],
    exclude_filters: [],
    bonus_points: 0,
  };
  const BONUS_RATIO_INITIAL = {
    type: 'CROSS_BRANCH_RATIO_THRESHOLD',
    // source_filters kullanılmaz — kaynak görevin kendi metriğidir
    target_filters: [{ ...EMPTY_BONUS_FILTER }],
    ratio_threshold: 0.5,  // çarpan: kaynak_sayısı >= hedef_sayısı × çarpan
    bonus_points: 0,
  };

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  // Bonus filtre yardımcıları
  const updateBonusFilter = (leg, idx, patch) => {
    const bc = form.bonus_condition;
    const arr = [...(bc[leg] || [])];
    arr[idx] = { ...arr[idx], ...patch };
    set('bonus_condition', { ...bc, [leg]: arr });
  };
  const addBonusFilter = (leg) => {
    const bc = form.bonus_condition;
    set('bonus_condition', { ...bc, [leg]: [...(bc[leg] || []), { ...EMPTY_BONUS_FILTER }] });
  };
  const removeBonusFilter = (leg, idx) => {
    const bc = form.bonus_condition;
    const arr = (bc[leg] || []).filter((_, i) => i !== idx);
    set('bonus_condition', { ...bc, [leg]: arr });
  };
  const columnsForTable = chColumns[form.source_table] || [];
  // Çapraz tablo için ayrı kolon listeleri
  const numColumnsForTable = chColumns[form.formula_numerator_table] || columnsForTable;
  const denColumnsForTable = chColumns[form.formula_denominator_table] || columnsForTable;
  // Seçili dönemin segment_ids'i — görev alt-küme zorunluluğu UI'da da
  // enforce edilir. Dönem tenant-geneli (boş liste) ise görev serbestçe
  // daraltabilir; dolu ise görev ancak o alt-kümeyi seçebilir.
  const selectedPeriod = useMemo(
    () => periods.find((p) => p.id === Number(form.game_period_id)) || null,
    [periods, form.game_period_id],
  );
  const periodSegmentIds = useMemo(
    () =>
      Array.isArray(selectedPeriod?.segment_ids)
        ? selectedPeriod.segment_ids.map((v) => String(v))
        : [],
    [selectedPeriod],
  );

  // Dönem değişirse görevin segment_ids'ini dönem kapsamına sığdır
  // (dönem spesifik segmentlere kısıtlıysa eski seçimler filtrelenir).
  useEffect(() => {
    if (!periodSegmentIds.length) return;
    const filtered = (form.segment_ids || []).filter((s) =>
      periodSegmentIds.includes(String(s)),
    );
    if (filtered.length !== (form.segment_ids || []).length) {
      set('segment_ids', filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.game_period_id, periodSegmentIds.join(',')]);

  // Tüm sekmelerin şemalarını birden çalıştır. Hatalı ise ilk hatalı sekmeye
  // geç, toplu error state'i yaz ve toast göster. Tab header'daki kırmızı
  // nokta — her sekmenin kendi alan prefix'lerine göre işaretlenir.
  const validateAllTabs = () => {
    const merged = {};
    let firstInvalidTab = null;
    for (const t of TABS) {
      const schema = taskWizardStepSchemas[t.schemaIdx];
      if (!schema) continue;
      const payload =
        t.key === 'tiers'
          ? { tiers: form.tiers, max_points: form.max_points, metric_type: form.metric_type }
          : form;
      const { ok, errors: errs } = validate(schema, payload);
      if (!ok) {
        Object.assign(merged, errs);
        if (!firstInvalidTab) firstInvalidTab = t.key;
      }
    }
    return { ok: !firstInvalidTab, errors: merged, firstInvalidTab };
  };

  const metricInfo = useMemo(() => METRIC_TYPES.find((m) => m.value === form.metric_type), [
    form.metric_type,
  ]);

  const updateTier = (idx, patch) => {
    const next = form.tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    set('tiers', next);
  };
  // Yeni eşik kademesi eklerken `threshold_value`'yu son satırın bir üzerine
  // otomatik konumlandır (örn. son eşik 100 ise yenisi 101). Boş listede 1.
  // Aspara kademeleri (`addTier` Periods'ta) ile aynı UX — kullanıcı 0'lı
  // satıra başlamak yerine önceki değerden devam ediyor olarak görüyor.
  const addTier = () => {
    const last = form.tiers.length
      ? Number(form.tiers[form.tiers.length - 1].threshold_value || 0)
      : 0;
    const nextThreshold = (Number.isFinite(last) && last > 0 ? last : 0) + 1;
    set('tiers', [
      ...form.tiers,
      {
        threshold_value: nextThreshold,
        threshold_operator: 'GTE',
        points: 0,
        sort_order: form.tiers.length + 1,
      },
    ]);
  };
  const removeTier = (idx) => set('tiers', form.tiers.filter((_, i) => i !== idx));

  // ---- Filtre satır editörleri (filters / exclude_filters) ----
  const FILTER_OPS = [
    { value: '=', label: '= (eşit)' },
    { value: '!=', label: '≠ (farklı)' },
    { value: '>', label: '> (büyük)' },
    { value: '>=', label: '≥' },
    { value: '<', label: '< (küçük)' },
    { value: '<=', label: '≤' },
    { value: 'IN', label: 'IN (listeden)' },
    { value: 'NOT IN', label: 'NOT IN' },
  ];
  // Hariç Tutulacak Filtreler zaten "bu koşula uyan satırları ÇIKAR" anlamına
  // gelir (backend pozitif eşleşmeyi düşer / RATIO'da `AND NOT (...)` uygular).
  // Bu yüzden hariç editöründe olumsuzlayan operatörler (≠, NOT IN) gizlenir —
  // aksi halde "çıkar + NOT IN" çift-olumsuz olur ve istenenin tersini tutar
  // (örn. NOT IN [00-Toplam] → 00-Toplam'ı tutar). Sadece pozitif eşleşme.
  const FILTER_OPS_EXCLUDE = FILTER_OPS.filter(
    (o) => o.value !== '!=' && o.value !== 'NOT IN',
  );
  const filterRows = (key) => (Array.isArray(form[key]) ? form[key] : []);
  const updateFilter = (key, idx, patch) => {
    const next = filterRows(key).map((f, i) => (i === idx ? { ...f, ...patch } : f));
    set(key, next);
  };
  const addFilter = (key) =>
    set(key, [...filterRows(key), { column: '', op: '=', value: '' }]);
  const removeFilter = (key, idx) =>
    set(key, filterRows(key).filter((_, i) => i !== idx));

  // Sistem sabiti picker'i icin state: hangi filter key + index aciktir.
  // null = kapali. Secim yapildiginda o filtrenin value'suna `${KEY}` yazilir.
  const [constPicker, setConstPicker] = useState(null);
  // Deger picker'i — CH'den DISTINCT degerleri cekip listeleyen modal.
  // null = kapali. Secim yapildiginda filtrenin value'suna CH'den gelen
  // gercek deger (veya IN icin virgullu liste) yazilir.
  const [valuePicker, setValuePicker] = useState(null);
  // Kaynak&Metrik + Puanlama sekmelerindeki panel açık/kapalı durumu.
  const [openPanels, setOpenPanels] = useState({
    metric: true,
    filters: true,
    tiers: true,
    bonus: false, // Bonus opsiyonel → varsayılan kapalı
  });
  const togglePanel = (key) => setOpenPanels((p) => ({ ...p, [key]: !p[key] }));

  // ---- Dönem (tarih / yıl-ay) filtresi — 2026-09 AS kararı ----
  // Motor hiçbir AS tablosu için otomatik tarih filtresi uygulamaz; dönem
  // görevin KENDİ filtresinden gelir. Kural backend ile birebir
  // (utils/validation.PERIOD_FILTER_RULES). Varsayılan değerler motorun eski
  // önceliğiyle aynı: görev geçerlilik tarihi (Temel Bilgi) → bağlı dönemin
  // başlangıç/bitişi. Değerler STRING yazılır (CH sabit string'i kolon tipine
  // çevirir; tam sayı sabiti String kolonda eşleşmez).
  const pad2 = (n) => String(n).padStart(2, '0');
  const isoDate = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  const addDaysISO = (ymd, n) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
    if (!m) return '';
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n));
    return isoDate(d);
  };
  // Görevin tarih aralığı: görev tarihi → dönem tarihi. Bitiş DAHİL; tarih
  // filtresinde '<' için ertesi gün (DateTime kolonda son günün saatleri kaçmasın).
  const taskDateRange = useMemo(() => {
    const start = String(form.effective_start_date || selectedPeriod?.start_date || '').slice(0, 10);
    const end = String(form.effective_end_date || selectedPeriod?.end_date || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) return null;
    const m = /^(\d{4})-(\d{2})/.exec(start);
    return {
      start,
      end: /^\d{4}-\d{2}-\d{2}$/.test(end) ? end : '',
      endExclusive: /^\d{4}-\d{2}-\d{2}$/.test(end) ? addDaysISO(end, 1) : '',
      yil: Number(m[1]),
      ay: Number(m[2]),
    };
  }, [form.effective_start_date, form.effective_end_date, selectedPeriod?.start_date, selectedPeriod?.end_date]);
  const defaultPeriod = useMemo(() => {
    if (taskDateRange) return { yil: taskDateRange.yil, ay: taskDateRange.ay };
    const now = new Date();
    return { yil: now.getFullYear(), ay: now.getMonth() + 1 };
  }, [taskDateRange]);
  const fmtPeriod = (p) =>
    p && Number.isFinite(p.yil) && Number.isFinite(p.ay) ? `${p.yil}/${pad2(p.ay)}` : '?';
  const colOf = (row) => String(row?.column || '').trim().toLowerCase();
  const opOf = (row) => String(row?.op || '=').toUpperCase();
  // Kolon listesi (CH DESCRIBE) yüklenmemişse engellemeyelim → var say.
  const tableHasColumn = (table, col) => {
    const cols = chColumns[table];
    if (!Array.isArray(cols) || cols.length === 0) return true;
    return cols.some((c) => String(c.key || '').toLowerCase() === col);
  };
  // Filtre anahtarı → hangi tablo (çapraz RATIO'da bacak tabloları)
  const tableForFilterKey = (key) => {
    const cross = form.metric_type === 'RATIO' && Boolean(form.is_cross_table_ratio);
    if (cross && key.startsWith('numerator')) return form.formula_numerator_table || form.source_table;
    if (cross && key.startsWith('denominator')) return form.formula_denominator_table || form.source_table;
    return form.source_table;
  };
  // Tabloda gerçekten bulunan ilk kural seçeneği (ör. prim'de yil_no/ay_no henüz yok → tarih)
  const choosePeriodOption = (table, rule) =>
    rule.options.find((o) => o.every((c) => tableHasColumn(table, c))) || rule.options[0];
  // Bir satır için görev/dönem tarihinden türeyen değer (+ normalize op); ilgisizse null.
  const derivedPeriodValue = (row, rule) => {
    if (!taskDateRange) return null;
    const col = colOf(row);
    const op = opOf(row);
    for (const opt of rule.options) {
      if (!opt.includes(col)) continue;
      if (opt.length === 2) {
        if (op !== '=') return null;
        return { value: String(col === opt[0] ? taskDateRange.yil : taskDateRange.ay), op: '=' };
      }
      if (op === '>=' || op === '>') return { value: taskDateRange.start, op: '>=' };
      if (op === '<' || op === '<=') return taskDateRange.endExclusive ? { value: taskDateRange.endExclusive, op: '<' } : null;
      return null;
    }
    return null;
  };
  const periodRowsFor = (option) => {
    if (option.length === 2) {
      return [
        { column: option[0], op: '=', value: taskDateRange ? String(taskDateRange.yil) : '' },
        { column: option[1], op: '=', value: taskDateRange ? String(taskDateRange.ay) : '' },
      ];
    }
    return [
      { column: option[0], op: '>=', value: taskDateRange?.start || '' },
      { column: option[0], op: '<', value: taskDateRange?.endExclusive || '' },
    ];
  };
  // Otomatik doldurulan değerlerin kaydı: {"key|col|op": değer}. Kullanıcı elle
  // değiştirdiyse (değer kayıtla eşleşmiyor) sonraki dönem değişimlerinde dokunulmaz.
  const autoFilledRef = useRef({});
  const autoKey = (key, row) => `${key}|${colOf(row)}|${opOf(row)}`;
  // Eksik dönem satırlarını listenin başına ekler (kısayol butonu).
  const addPeriodFilters = (key, table) => {
    const rule = periodFilterRule(table);
    if (!rule) return;
    const rows = filterRows(key);
    const st = periodFilterStatus(table, rows);
    if (!st || st.ok) return;
    if (st.empty.length) {
      syncPeriodFilters(key, table);
      return;
    }
    const option = choosePeriodOption(table, rule);
    const existing = new Set(rows.map(colOf));
    const add = periodRowsFor(option).filter((r) => !existing.has(r.column));
    add.forEach((r) => { autoFilledRef.current[autoKey(key, r)] = r.value; });
    set(key, [...add, ...rows]);
    setOpenPanels((p) => ({ ...p, filters: true }));
  };
  // Mevcut dönem satırlarını görev/dönem tarihine eşitler (buton).
  const syncPeriodFilters = (key, table) => {
    const rule = periodFilterRule(table);
    if (!rule || !taskDateRange) return;
    set(
      key,
      filterRows(key).map((r) => {
        const d = derivedPeriodValue(r, rule);
        if (!d) return r;
        autoFilledRef.current[autoKey(key, { ...r, op: d.op })] = d.value;
        return { ...r, op: d.op, value: d.value };
      }),
    );
  };
  // Görev tarihi / dönem değişince: BOŞ ya da daha önce otomatik doldurulmuş
  // (elle değiştirilmemiş) dönem satırlarını yeni tarihlerle doldur.
  const crossMode = form.metric_type === 'RATIO' && Boolean(form.is_cross_table_ratio);
  const periodLegs = crossMode
    ? [['numerator_filters', form.formula_numerator_table], ['denominator_filters', form.formula_denominator_table]]
    : [['filters', form.source_table]];
  useEffect(() => {
    if (!taskDateRange) return;
    for (const [key, table] of periodLegs) {
      const rule = periodFilterRule(table);
      if (!rule) continue;
      let changed = false;
      const next = filterRows(key).map((r) => {
        const d = derivedPeriodValue(r, rule);
        if (!d) return r;
        const cur = r.value === null || r.value === undefined ? '' : String(r.value);
        const k = autoKey(key, r);
        const wasAuto = cur === '' || cur === String(autoFilledRef.current[k] ?? '\u0000');
        if (!wasAuto || (cur === d.value && r.op === d.op)) return r;
        autoFilledRef.current[autoKey(key, { ...r, op: d.op })] = d.value;
        changed = true;
        return { ...r, op: d.op, value: d.value };
      });
      if (changed) set(key, next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskDateRange?.start, taskDateRange?.endExclusive, form.source_table,
      form.formula_numerator_table, form.formula_denominator_table, crossMode, form.metric_type]);
  // Filtre satırında kolon seçilince: tarih kolonunda '=' → '>='; kural kolonuysa
  // ve satır boşsa görev/dönem tarihinden doldur; tarih kolonunda '<' bitiş satırı
  // yoksa ekle (bitiş dahil olsun diye ertesi gün).
  const onFilterColumnChange = (key, idx, newCol, colMeta) => {
    const table = tableForFilterKey(key);
    const rule = periodFilterRule(table);
    const rows = filterRows(key);
    const row = rows[idx] || {};
    const fresh = row.value === '' || row.value === null || row.value === undefined;
    let op = row.op || '=';
    if (isDateColumn(colMeta) && op === '=') op = '>=';
    let value = row.value ?? '';
    let extra = null;
    if (rule && fresh) {
      const d = derivedPeriodValue({ column: newCol, op }, rule);
      if (d) {
        op = d.op;
        value = d.value;
        autoFilledRef.current[autoKey(key, { column: newCol, op })] = value;
        const isDateOpt = rule.options.some((o) => o.length === 1 && o[0] === String(newCol).toLowerCase());
        const hasEnd = rows.some((r, n) => n !== idx && colOf(r) === String(newCol).toLowerCase() && ['<', '<='].includes(opOf(r)));
        if (isDateOpt && op === '>=' && !hasEnd && taskDateRange?.endExclusive) {
          extra = { column: newCol, op: '<', value: taskDateRange.endExclusive };
          autoFilledRef.current[autoKey(key, extra)] = extra.value;
        }
      }
    }
    const next = rows.map((r, n) => (n === idx ? { ...r, column: newCol, op, value } : r));
    if (extra) next.splice(idx + 1, 0, extra);
    set(key, next);
  };
  // Uyarı kutusu: dönem filtresi EKSİK (kırmızı, kaydetme engellenir), DEĞERİ BOŞ
  // (sarı; dönem seçilince otomatik dolar) ya da seçili dönemle UYUŞMUYOR (sarı).
  // Gösterilecek bir şey yoksa hiçbir şey çizmez. Kural dışı tablolarda pasif.
  const renderPeriodFilterNotice = ({ table, filterKey, className = '' }) => {
    const rule = periodFilterRule(table);
    if (!rule) return null;
    const rows = filterRows(filterKey);
    const st = periodFilterStatus(table, rows);
    const current = st?.ok ? periodFromFilters(filterKey, rule) : null;
    const mismatch =
      Boolean(current) && Boolean(taskDateRange) &&
      (current.yil !== defaultPeriod.yil || current.ay !== defaultPeriod.ay);
    if (!st || (st.ok && !mismatch)) return null;
    const periodLabel = fmtPeriod(defaultPeriod);
    const rangeLabel = taskDateRange
      ? `${taskDateRange.start}${taskDateRange.end ? ` – ${taskDateRange.end}` : ''}`
      : 'görev tarihi / dönem seçilmedi';
    let box;
    if (st.missing.length) {
      box = (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900 space-y-2">
          <p className="font-semibold">
            {String(table).toLowerCase()} {rule.label} — {periodFilterRequirement(table)} filtresi zorunlu
          </p>
          <p className="text-xs text-rose-800">
            Motor otomatik tarih filtresi uygulamaz; görevin dönem kapsamı bu filtreyle belirlenir.
            Dönem filtresi olmadan görev tablodaki <strong>tüm dönemleri</strong> toplar; bu haliyle
            kaydedilemez. Eksik: {st.missing.map((c) => c.toUpperCase()).join(', ')}.
          </p>
          <Button size="sm" variant="secondary" icon="plus" onClick={() => addPeriodFilters(filterKey, table)}>
            Dönem filtresini ekle ({rangeLabel})
          </Button>
        </div>
      );
    } else if (st.empty.length) {
      box = (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-2">
          <p className="font-semibold">
            Dönem filtresinin değeri boş: {st.empty.map((c) => c.toUpperCase()).join(', ')}
          </p>
          <p className="text-xs text-amber-800">
            Temel Bilgi sekmesinde görev tarihi ya da dönem seçildiğinde otomatik dolar; elle de girebilirsiniz.
          </p>
          {taskDateRange && (
            <Button size="sm" variant="secondary" onClick={() => syncPeriodFilters(filterKey, table)}>
              Şimdi doldur ({rangeLabel})
            </Button>
          )}
        </div>
      );
    } else {
      box = (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-2">
          <p className="font-semibold">
            Dönem filtresi ({fmtPeriod(current)}) görevin tarih aralığıyla ({periodLabel}) uyuşmuyor
          </p>
          <p className="text-xs text-amber-800">
            Bilerek farklı bir dönemi hedeflemiyorsanız filtreyi görev/dönem tarihine eşitleyin.
          </p>
          <Button size="sm" variant="secondary" onClick={() => syncPeriodFilters(filterKey, table)}>
            Döneme eşitle ({rangeLabel})
          </Button>
        </div>
      );
    }
    return className ? <div className={className}>{box}</div> : box;
  };
  // Filtrelerden görevin dönemi: önce yıl/ay çifti, yoksa tarih alt sınırı (>= / >).
  const periodFromFilters = (key, rule) => {
    const rows = filterRows(key);
    for (const opt of rule.options) {
      if (opt.length === 2) {
        const y = rows.find((r) => colOf(r) === opt[0] && opOf(r) === '=');
        const m = rows.find((r) => colOf(r) === opt[1] && opOf(r) === '=');
        if (y && m && y.value !== '' && m.value !== '') return { yil: Number(y.value), ay: Number(m.value) };
      } else {
        const lo = rows.find((r) => colOf(r) === opt[0] && ['>=', '>'].includes(opOf(r)));
        const mm = /^(\d{4})-(\d{2})/.exec(String(lo?.value || ''));
        if (mm) return { yil: Number(mm[1]), ay: Number(mm[2]) };
      }
    }
    return null;
  };

  const renderFilterRowsEditor = ({ title, hint, filterKey, tone = 'neutral', columns: columnsOverride }) => {
    const rows = filterRows(filterKey);
    // Kaydetme bu listede değeri boş satır yüzünden durduysa o satırlar işaretlenir;
    // liste değişince (set) hata temizlenir.
    const listHasError = Boolean(errors[filterKey]);
    // columnsOverride: çapraz tablo modunda her leg kendi kolon listesini gönderir
    const cols = columnsOverride || columnsForTable;
    // Hariç (exclude) filtre setlerinde olumsuzlayan operatörleri gizle —
    // çift-olumsuz tuzağını önlemek için (bkz. FILTER_OPS_EXCLUDE).
    const isExclude = typeof filterKey === 'string' && filterKey.includes('exclude');
    const opOptions = isExclude ? FILTER_OPS_EXCLUDE : FILTER_OPS;
    const toneClass =
      tone === 'danger'
        ? 'border-rose-200 bg-rose-50/40'
        : 'border-neutral-200 bg-neutral-50';
    return (
      <div className={`rounded-lg border ${toneClass} p-3 space-y-2`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-neutral-800">{title}</p>
            {hint && <p className="text-[11px] text-neutral-500">{hint}</p>}
          </div>
          <Button
            size="sm"
            variant="secondary"
            icon="plus"
            onClick={() => addFilter(filterKey)}
          >
            Filtre Ekle
          </Button>
        </div>
        {rows.length === 0 && (
          <p className="text-xs text-neutral-400 italic">Tanımlı filtre yok.</p>
        )}
        {rows.map((f, i) => {
          const isRef =
            typeof f.value === 'string' &&
            f.value.startsWith('${') &&
            f.value.endsWith('}');
          // "Değerden Seç" picker'i yalnızca (1) kaynak tablo secilmis,
          // (2) kolon secilmis ve (3) op sistem-sabit referansi degilse
          // aktif. Sistem sabiti referansi (${...}) ile CH DISTINCT
          // karisir — admin sabit gorevleri sabitle, ayri degerleri
          // CH'den sec; ayri UX path'leri.
          const canPickValue = Boolean(
            form.source_table && f.column && !isRef,
          );
          // Değer kutusu kolon tipine göre: tarih → date picker, sayı → number, diğer → metin.
          const inputType = filterValueInputType(
            cols.find((c) => c.key === f.column),
            f.op,
            f.value,
          );
          return (
            <div
              key={i}
              className="grid grid-cols-[1.2fr_0.9fr_1.2fr_auto_auto_auto] items-center gap-2"
            >
              <Select
                value={f.column || ''}
                onChange={(e) =>
                  onFilterColumnChange(
                    filterKey,
                    i,
                    e.target.value,
                    cols.find((c) => c.key === e.target.value),
                  )
                }
                placeholder="Kolon"
                options={cols.map((c) => ({ value: c.key, label: c.label }))}
              />
              <Select
                value={f.op || '='}
                onChange={(e) => updateFilter(filterKey, i, { op: e.target.value })}
                options={opOptions}
              />
              <div className="min-w-0">
                <Input
                  type={inputType}
                  allowNegative
                  placeholder={
                    isRef
                      ? 'Sistem sabitine referans'
                      : inputType === 'date'
                        ? 'YYYY-AA-GG'
                        : inputType === 'number'
                          ? 'Sayı'
                          : 'Değer (IN için virgülle)'
                  }
                  value={f.value ?? ''}
                  onChange={(e) => updateFilter(filterKey, i, { value: e.target.value })}
                  className={cn(
                    isRef && 'font-mono text-[11px] text-primary-700',
                    listHasError && isFilterValueMissing(f) && 'ring-1 ring-danger-400',
                  )}
                />
                {/* Parametre referansı seçilmişse altında gerçek değerleri göster */}
                {isRef && (() => {
                  const key = f.value.slice(2, -1); // "${KEY}" → "KEY"
                  const param = systemParams.find((p) => p.param_key === key);
                  if (!param) return null;
                  const v = param.param_value;
                  const preview = Array.isArray(v)
                    ? v.join(', ')
                    : String(v);
                  return (
                    <p className="mt-0.5 text-[10px] text-neutral-400 truncate" title={preview}>
                      → {preview}
                    </p>
                  );
                })()}
              </div>
              <button
                type="button"
                title={
                  canPickValue
                    ? 'Değerden seç (CH DISTINCT)'
                    : 'Önce tablo ve kolon seç'
                }
                onClick={() =>
                  canPickValue &&
                  setValuePicker({
                    filterKey,
                    index: i,
                    table: form.source_table,
                    column: f.column,
                    op: f.op || '=',
                    current: f.value ?? '',
                  })
                }
                disabled={!canPickValue}
                className={`p-1.5 rounded-md transition-colors ${
                  canPickValue
                    ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
                    : 'text-neutral-300 cursor-not-allowed'
                }`}
                aria-label="Değerden seç"
              >
                <Icon name="search" className="h-4 w-4" />
              </button>
              <button
                type="button"
                title="Sistem sabitinden seç"
                onClick={() => setConstPicker({ filterKey, index: i })}
                className="p-1.5 text-primary-500 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                aria-label="Sistem sabitinden seç"
              >
                <Icon name="settings" className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => removeFilter(filterKey, i)}
                className="p-1.5 text-neutral-400 hover:text-danger-500"
              >
                <Icon name="trash" className="h-4 w-4" />
              </button>
              {listHasError && isFilterValueMissing(f) && (
                <p className="col-start-3 col-span-4 -mt-1 text-[10px] text-danger-600">
                  Değer girin ya da satırı silin.
                </p>
              )}
              {inputType === 'date' && (
                <p className="col-start-3 col-span-4 -mt-1 text-[10px] text-neutral-400">
                  Gün aralığı için iki satır: <span className="font-mono">&gt;=</span> başlangıç,{' '}
                  <span className="font-mono">&lt;</span> bitiş (bitiş günü hariç).
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Görsel yükleme yardımcıları ──────────────────────────────────────────
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

  const handleImageSelect = async (file) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      dispatch(pushToast({ kind: 'error', message: 'Sadece JPG, PNG, WebP veya GIF yükleyebilirsiniz' }));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      dispatch(pushToast({ kind: 'error', message: `Dosya çok büyük (${Math.round(file.size/1024)} KB). Limit 5 MB.` }));
      return;
    }
    if (mode === 'edit' && initial?.id) {
      setImageSaving(true);
      try {
        const res = await tasksApi.uploadImage(initial.id, file);
        const newUrl = res.data?.data?.image_url || res.data?.image_url;
        setImagePreview(newUrl ? API_BASE_URL + newUrl : URL.createObjectURL(file));
        setImageFile(null);
        dispatch(pushToast({ kind: 'success', message: 'Görsel güncellendi' }));
      } catch (_e) {
        dispatch(pushToast({ kind: 'error', message: 'Görsel yüklenemedi' }));
      } finally {
        setImageSaving(false);
      }
    } else {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setImageDeletePending(false);
    }
  };

  const handleImageDelete = async () => {
    if (mode === 'edit' && initial?.id && imagePreview && !imageFile) {
      setImageSaving(true);
      try {
        await tasksApi.deleteImage(initial.id);
        setImagePreview(null);
        dispatch(pushToast({ kind: 'success', message: 'Görsel silindi' }));
      } catch (_e) {
        dispatch(pushToast({ kind: 'error', message: 'Görsel silinemedi' }));
      } finally {
        setImageSaving(false);
      }
    } else {
      if (imageFile) URL.revokeObjectURL(imagePreview);
      setImageFile(null);
      setImagePreview(null);
      setImageDeletePending(true);
    }
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    setImageDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleImageSelect(file);
  };

  const handleSubmit = async () => {
    // Tüm sekmeleri doğrula; hatalı ise ilk hatalı sekmeye geç ve kaydetme.
    const { ok, errors: allErrors, firstInvalidTab } = validateAllTabs();
    if (!ok) {
      setErrors(allErrors);
      if (firstInvalidTab) setActiveTab(firstInvalidTab);
      dispatch(pushToast({ kind: 'error', message: firstError(allErrors) || 'Eksik alan var' }));
      return;
    }

    // Dönem sınırı: effective_end_date dönemin bitiş tarihini aşamaz.
    if (
      form.effective_end_date &&
      selectedPeriod?.end_date &&
      form.effective_end_date > selectedPeriod.end_date
    ) {
      setErrors({ effective_end_date: `Dönem bitiş tarihini (${selectedPeriod.end_date}) aşamaz` });
      setActiveTab('basic');
      dispatch(pushToast({ kind: 'error', message: 'Görev bitiş tarihi dönem dışına taşıyor' }));
      return;
    }

    setErrors({});
    setSaving(true);
    const payload = { ...form };
    // Boş string tarihleri backend'e göndermeyelim (date bekliyor)
    if (!payload.effective_start_date) payload.effective_start_date = null;
    if (!payload.effective_end_date) payload.effective_end_date = null;
    // MANUAL metric tipinde bonus koşulu mantıken çalışmaz (CH metric verisi
    // yok); UI'da panel zaten gizli — payload'da da defansif olarak null'a
    // çek (eski kayıttan kalmış olabilir).
    if (payload.metric_type === 'MANUAL') {
      payload.bonus_condition = null;
    }
    // max_points senkronizasyonu — UI sadeleştirmesi:
    //   - MANUAL'da kullanıcı Puanlama sekmesindeki Max Puan input'undan giriyor
    //     (eşik tablosu yok, türetilemez). payload.max_points olduğu gibi
    //     bırakılıyor.
    //   - Diğer tüm tipler (COUNT/SUM/RATIO/RANK) için max_points artık
    //     eşiklerin (tier.points) en yükseğinden otomatik türetiliyor.
    //     Bu, "Temel Bilgi'deki Max Puan ile eşik max'ı eşleşmiyor" tutarsızlık
    //     bug'ını ortadan kaldırıyor (önceden iki alan birbirinden bağımsızdı).
    if (payload.metric_type !== 'MANUAL') {
      const tierPoints = (Array.isArray(payload.tiers) ? payload.tiers : [])
        .map((t) => Number(t?.points) || 0);
      payload.max_points = tierPoints.length > 0 ? Math.max(0, ...tierPoints) : 0;
    }
    // Bonus koşulu: checkbox işaretsizse null, işaretli ama tüm alanlar boşsa yine null
    if (!payload.bonus_condition) {
      payload.bonus_condition = null;
    } else {
      const bc = payload.bonus_condition;
      const bcType = (bc.type || 'SIMPLE').toUpperCase();
      let empty;
      if (bcType === 'CROSS_BRANCH_RATIO_THRESHOLD') {
        // Kaynak = görevin metriği; sadece hedef filtresi ve çarpan yeterli
        const tfFilled = (bc.target_filters || []).some((f) => f.column && f.op);
        const legacyFilled = bc.target?.value; // eski format
        empty = !tfFilled && !legacyFilled && !bc.bonus_points;
      } else {
        // Yeni çoklu-filtre yapısı
        const filtersFilled = (bc.filters || []).some((f) => f.column && f.op);
        // Legacy fallback
        const legacyFilled = bc.column && bc.op;
        empty = !filtersFilled && !legacyFilled && !bc.bonus_points;
      }
      if (empty) payload.bonus_condition = null;
    }
    // Flat form alanlarını (source_table / metric_column / formula_*)
    // backend'in beklediği nested `source` objesine dönüştür.
    const backendPayload = toBackendPayload(payload);
    const action = mode === 'edit'
      ? updateTask({ id: initial.id, payload: backendPayload })
      : createTask(backendPayload);
    const res = await dispatch(action);
    setSaving(false);
    if (res.meta?.requestStatus === 'fulfilled') {
      const savedTaskId = res.payload?.id || initial?.id;
      if (savedTaskId && imageFile) {
        try {
          await tasksApi.uploadImage(savedTaskId, imageFile);
        } catch (_e) {
          dispatch(pushToast({ kind: 'warning', message: 'Görev kaydedildi, görsel yüklenemedi' }));
        }
      }
      dispatch(
        pushToast({
          kind: 'success',
          message: mode === 'edit' ? 'Görev güncellendi' : 'Görev oluşturuldu',
        }),
      );
      onClose();
    } else {
      // Backend tarafından gelen detaylı hata: tasksSlice artık {message, code,
      // detail, status} normalize edilmiş bir payload yolluyor (eski hâlinde
      // sadece string idi). Toast'a Türkçe mesajı, ilgili form alanına
      // BusinessRuleError detaylarını yansıt.
      const errPayload = (res.payload && typeof res.payload === 'object')
        ? res.payload
        : { message: typeof res.payload === 'string' ? res.payload : 'Kaydedilemedi' };
      const detail = errPayload.detail || {};
      const msg = errPayload.message || 'Kaydedilemedi';

      dispatch(pushToast({ kind: 'error', message: msg }));

      // Backend hangi alanın bozuk olduğunu söylüyor mu?
      //   • detail.field   → "metric_column", "metric_type"
      //   • detail.column  → kolon adı (RATIO leg uyumsuzluğunda)
      //   • detail.context → "pay (numerator)" | "payda (denominator)"
      let fieldKey = detail.field;
      if (!fieldKey && detail.column) {
        if (detail.context === 'pay (numerator)') fieldKey = 'formula_numerator';
        else if (detail.context === 'payda (denominator)') fieldKey = 'formula_denominator';
        else fieldKey = 'metric_column';
      }
      if (fieldKey) {
        setErrors((prev) => ({ ...prev, [fieldKey]: msg }));
        // metric_column / formula_* alanları "Kaynak & Metrik" sekmesinde
        setActiveTab('source');
      }
    }
  };

  // Tab header'daki kırmızı nokta indikatörü — her sekmenin field prefix'lerine
  // göre errors state'inde hata var mı kontrol eder.
  const tabsWithErrors = TABS.map((t) => ({
    key: t.key,
    label: t.label,
    hasError: tabHasError(t.key, errors),
  }));

  return (
    <>
    <Modal
      open={!!open}
      onClose={onClose}
      closeOnBackdrop={false}
      title={
        mode === 'edit'
          ? 'Görev Düzenle'
          : template
            ? `Yeni Görev — ${template.label}`
            : 'Yeni Görev'
      }
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            İptal
          </Button>
          <Button loading={saving} onClick={handleSubmit}>
            {mode === 'edit' ? 'Güncelle' : 'Oluştur'}
          </Button>
        </>
      }
    >
      <div className="mb-6">
        <Tabs tabs={tabsWithErrors} value={activeTab} onChange={setActiveTab} />
      </div>

      {activeTab === 'basic' && (
        <div className="space-y-4">
          <Input
            label="Görev Adı"
            required
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="HYO Hedefi"
            error={errors.name}
          />
          <Textarea
            label="Açıklama"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            rows={2}
          />
          <Select
            label="Dönem"
            required
            value={form.game_period_id}
            onChange={(e) => set('game_period_id', Number(e.target.value))}
            placeholder="Dönem seçin"
            options={periods.map((p) => ({ value: p.id, label: p.name }))}
            error={errors.game_period_id}
          />
          {/* Max Puan input'u Temel Bilgi'den kaldırıldı.
              Submit'te otomatik hesaplanıyor:
                COUNT / SUM / RATIO / RANK → max(tier.points)  (Puanlama sekmesi)
                MANUAL                       → Puanlama sekmesindeki Max Puan input'u
              Bu yaklaşım eşik tablosu ile max_points arasındaki tutarsızlık
              riskini ortadan kaldırıyor (önce 25 puanlık tier giriyordun ama
              max_points 20 yazıyordun → liderlik tablosu hesabı bozuluyordu). */}
          {/* Joker seçeneği yalnızca RANK tipi görevler için geçerlidir.
              Backend is_joker=true'yu yalnızca RANK'ta kullanır: global sıralama
              yerine grup içi (müdürlük başına ayrı) sıralama yapar. */}
          {form.metric_type === 'RANK' && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_joker}
                onChange={(e) => set('is_joker', e.target.checked)}
                className="rounded border-neutral-300"
              />
              <span>
                Joker görev — grup içi sıralama
                <span className="ml-1 text-[11px] text-neutral-400">
                  (her müdürlük kendi içinde 1., 2., 3. sıralanır)
                </span>
              </span>
            </label>
          )}

          {/* Hedefleme — Kullanıcı Tipi (Segment) & Müdürlükler opsiyonel */}
          <div className="rounded-xl border border-teal-200 overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 bg-teal-50">
              <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-teal-500 text-white">
                <Icon name="users" className="h-3 w-3" />
              </span>
              <div>
                <p className="text-sm font-semibold text-teal-900 leading-tight">
                  Hedefleme
                  <span className="ml-2 text-[11px] font-normal text-teal-600">(opsiyonel)</span>
                </p>
                <p className="text-[11px] text-teal-600 mt-0.5">
                  Boş = dönemden devral · dolu = dönemin alt kümesine daralt
                </p>
              </div>
            </div>
            <div className="border-t border-teal-200 bg-white p-3 space-y-3">

            <MultiSelectChips
              label="Kullanıcı Tipi (Segment)"
              loading={segmentsStatus === 'loading'}
              options={segments
                .filter((s) =>
                  periodSegmentIds.length === 0
                    ? true
                    : periodSegmentIds.includes(s.name || String(s.id)),
                )
                .map((s) => ({
                  value: s.name || String(s.id),
                  label: s.name || `#${s.id}`,
                  hint: s.is_active === false ? '(pasif)' : undefined,
                }))}
              value={form.segment_ids}
              onChange={(next) => set('segment_ids', next)}
              hint={
                periodSegmentIds.length
                  ? 'Boş = dönem kapsamı devralınır. Dolu = bu kullanıcı tiplerine daralt (dönemin alt kümesi).'
                  : 'Boş = dönem tenant geneline açık; tüm kullanıcı tipleri dahil. Dolu = seçilenlere daralt.'
              }
              emptyLabel="Tenant için tanımlı kullanıcı tipi yok"
              error={errors.segment_ids}
            />
            <details className="rounded-lg border border-neutral-200 bg-white">
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-neutral-700 select-none flex items-center justify-between">
                <span>
                  Müdürlükler
                  <span className="ml-2 text-xs font-normal text-neutral-400">(opsiyonel)</span>
                  {form.mudurluk_kodlari?.length > 0 && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
                      {form.mudurluk_kodlari.length} seçili
                    </span>
                  )}
                </span>
              </summary>
              <div className="border-t border-neutral-200 p-3">
                <MultiSelectChips
                  label=""
                  loading={mudurluklerStatus === 'loading'}
                  options={mudurlukler.map((m) => ({
                    value: m.code,
                    label: m.code,
                    hint: m.user_count ? `${m.user_count} kullanıcı` : undefined,
                  }))}
                  value={form.mudurluk_kodlari}
                  onChange={(next) => set('mudurluk_kodlari', next)}
                  hint="Boş = dönemden devral. Dolu = bu müdürlüklere daralt."
                  emptyLabel="Tenant için tanımlı müdürlük yok"
                  error={errors.mudurluk_kodlari}
                />
              </div>
            </details>
            </div>{/* /body */}
          </div>{/* /panel */}

          {/* Geçerlilik tarihleri — opsiyonel */}
          <details className="rounded-xl border border-neutral-200 overflow-hidden bg-white">
            <summary className="cursor-pointer flex items-center gap-3 px-4 py-3 bg-neutral-50 hover:bg-neutral-100/60 transition-colors select-none list-none">
              <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-neutral-400 text-white">
                <Icon name="calendar" className="h-3 w-3" />
              </span>
              <div>
                <p className="text-sm font-semibold text-neutral-800 leading-tight">
                  Geçerlilik Tarihleri
                  <span className="ml-2 text-[11px] font-normal text-neutral-400">(opsiyonel)</span>
                </p>
                <p className="text-[11px] text-neutral-500 mt-0.5">
                  Boş = dönem tarihleri kullanılır
                </p>
              </div>
              {(form.effective_start_date || form.effective_end_date) && (
                <span className="ml-auto rounded-full bg-neutral-200 px-2 py-0.5 text-[11px] font-medium text-neutral-700">
                  özelleştirilmiş
                </span>
              )}
            </summary>
            <div className="border-t border-neutral-200 p-3">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Başlangıç Tarihi"
                  type="date"
                  value={form.effective_start_date || ''}
                  onChange={(e) => set('effective_start_date', e.target.value || '')}
                  error={errors.effective_start_date}
                  hint="Dönem içinde görevin aktifleştiği gün"
                />
                <Input
                  label="Bitiş Tarihi"
                  type="date"
                  value={form.effective_end_date || ''}
                  onChange={(e) => set('effective_end_date', e.target.value || '')}
                  error={errors.effective_end_date}
                  hint="Son dahil edilen gün"
                />
              </div>
              {/* Uyarı: görev bitiş tarihi dönem dışına taşıyorsa */}
              {form.effective_end_date && selectedPeriod?.end_date &&
                form.effective_end_date > selectedPeriod.end_date && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <Icon name="alert-triangle" className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">
                    Görev bitiş tarihi ({form.effective_end_date}), dönem bitiş tarihini (
                    {selectedPeriod.end_date}) aşıyor.{' '}
                    <strong>Dönem kapandıktan sonra bu görev hesaplanmaz</strong> — görev fiilen dönem bitişinde durur.
                  </p>
                </div>
              )}
            </div>
          </details>


          {/* ── Görsel ── */}
          <div className="rounded-xl border border-neutral-200 overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 bg-neutral-50">
              <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-neutral-400 text-white">
                <Icon name="image" className="h-3 w-3" />
              </span>
              <div>
                <p className="text-sm font-semibold text-neutral-800 leading-tight">
                  Görsel
                  <span className="ml-2 text-[11px] font-normal text-neutral-400">(opsiyonel)</span>
                </p>
                <p className="text-[11px] text-neutral-500 mt-0.5">JPG · PNG · WebP · GIF — maks. 5 MB</p>
              </div>
              {imageSaving && (
                <span className="ml-auto text-xs text-neutral-400 animate-pulse">Yükleniyor…</span>
              )}
            </div>
            <div className="border-t border-neutral-200 bg-white p-3">
              {imagePreview ? (
                <div className="flex items-start gap-3">
                  <div className="relative flex-shrink-0">
                    <img
                      src={imagePreview}
                      alt="Görev görseli"
                      className="rounded-lg object-cover border border-neutral-200"
                      style={{ width: 250, height: 200 }}
                    />
                    {imageFile && (
                      <span className="absolute top-1 left-1 rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow">
                        Kaydedilince yüklenecek
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 mt-1">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        onChange={(e) => handleImageSelect(e.target.files?.[0])}
                        disabled={imageSaving}
                      />
                      <span className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 transition-colors cursor-pointer">
                        <Icon name="upload" className="h-3 w-3" />
                        Değiştir
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={handleImageDelete}
                      disabled={imageSaving}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Icon name="trash-2" className="h-3 w-3" />
                      Sil
                    </button>
                  </div>
                </div>
              ) : (
                <label
                  className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed transition-colors cursor-pointer ${
                    imageDragOver ? 'border-primary-400 bg-primary-50' : 'border-neutral-300 bg-neutral-50 hover:bg-neutral-100/60'
                  }`}
                  style={{ minHeight: 120 }}
                  onDragOver={(e) => { e.preventDefault(); setImageDragOver(true); }}
                  onDragLeave={() => setImageDragOver(false)}
                  onDrop={handleImageDrop}
                >
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => handleImageSelect(e.target.files?.[0])}
                    disabled={imageSaving}
                  />
                  <Icon name="image" className="h-7 w-7 text-neutral-300" />
                  <p className="text-sm font-medium text-neutral-500">
                    {imageDragOver ? 'Bırakın…' : 'Tıkla veya sürükle bırak'}
                  </p>
                  <p className="text-[11px] text-neutral-400">JPG · PNG · WebP · GIF — maks. 5 MB</p>
                </label>
              )}
            </div>
          </div>

        </div>
      )}

      {activeTab === 'source' && (
        <div className="space-y-3">

          {/* ═══════════════════════════════════════════
              Panel 1 — Metrik & Kaynak Yapılandırması
          ════════════════════════════════════════════ */}
          <div className="rounded-xl border border-sky-200 overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-sky-50 hover:bg-sky-100/70 transition-colors text-left"
              onClick={() => togglePanel('metric')}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-sky-500 text-white">
                  <Icon name="chart" className="h-3 w-3" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-sky-900 leading-tight">Metrik & Kaynak Yapılandırması</p>
                  <p className="text-[11px] text-sky-600 mt-0.5">
                    Metrik tipi · Kaynak tablo · Kolon / formül tanımı
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {metricInfo && (
                  <span className="rounded-full bg-sky-200 px-2 py-0.5 text-[11px] font-medium text-sky-800">
                    {metricInfo.label}
                  </span>
                )}
                {form.source_table && (
                  <span className="rounded-full bg-sky-200 px-2 py-0.5 text-[11px] font-medium text-sky-800 max-w-[120px] truncate">
                    {form.source_table}
                  </span>
                )}
                <Icon
                  name={openPanels.metric ? 'chevron-up' : 'chevron-down'}
                  className="h-4 w-4 text-sky-500"
                />
              </div>
            </button>

            {openPanels.metric && (
              <div className="border-t border-sky-200 bg-white px-4 py-4 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-wide mb-2">Metrik Tipi</label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {METRIC_TYPES.map((m) => {
                const desc = METRIC_DESCRIPTIONS[m.value];
                const isActive = form.metric_type === m.value;
                const isOpen = tooltipMetric === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => {
                      set('metric_type', m.value);
                      // MANUAL seçilince eşikler anlamsız — temizle
                      if (m.value === 'MANUAL') {
                        set('tiers', []);
                      }
                      // COUNT modu satır sayar — metrik kolon kullanılmaz.
                      // Backend `metric_builder.build_aggregate("COUNT", ...)` `count()`
                      // döner, kolon parametresi `None` geçer. UI'da seçtirip kafa
                      // karıştırmamak için boşalt.
                      if (m.value === 'COUNT') {
                        set('metric_column', '');
                      }
                      // Yeni metric_type seçili kolonla uyumsuzsa kolonu sıfırla.
                      // Örn: SUM iken Float64 'premium' seçili; AVG'de hâlâ uyumlu —
                      // dokunma. Ama metin kolonu olsaydı temizlenir.
                      if (form.metric_column &&
                          selectionIsIncompatible(columnsForTable, form.metric_column, m.value)) {
                        set('metric_column', '');
                      }
                      // RANK dışı görevlerde varsayılan mod LINEAR; RANK için DISCRETE
                      if (!form.tier_mode || form.tier_mode === (form.metric_type === 'RANK' ? 'DISCRETE' : 'LINEAR')) {
                        set('tier_mode', m.value === 'RANK' ? 'DISCRETE' : 'LINEAR');
                      }
                      setTooltipMetric(null);
                    }}
                    className={
                      'rounded-lg border p-3 text-left transition-all ' +
                      (isActive
                        ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500/20'
                        : 'border-neutral-200 hover:border-neutral-300')
                    }
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-neutral-900">{m.label}</span>
                      {desc && (
                        <button
                          type="button"
                          tabIndex={-1}
                          title="Açıklama"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isOpen) {
                              setTooltipMetric(null);
                            } else {
                              // Butonun viewport konumunu al — fixed render için
                              const rect = e.currentTarget.getBoundingClientRect();
                              setTooltipPos({
                                top: rect.bottom + 6,
                                // Sağ taşmayı önle: sola hizala ama viewport dışına çıkma
                                left: Math.min(
                                  rect.left,
                                  window.innerWidth - 300,
                                ),
                              });
                              setTooltipMetric(m.value);
                            }
                          }}
                          className={
                            'rounded p-0.5 transition-colors ' +
                            (isOpen
                              ? 'text-primary-600'
                              : 'text-neutral-400 hover:text-primary-500')
                          }
                        >
                          <Icon name="info" className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="text-[10px] text-neutral-500">{m.hint}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {form.metric_type !== 'MANUAL' && (
            <>
              {/* Çapraz tablo modunda Kaynak Tablo her leg'de ayrıca seçildiği için gizlenir */}
              {!(form.metric_type === 'RATIO' && form.is_cross_table_ratio) && (
                <Select
                  label="Kaynak Tablo"
                  required
                  value={form.source_table}
                  onChange={(e) => set('source_table', e.target.value)}
                  placeholder="Kaynak tablo seçin"
                  options={chTables.map((t) => ({ value: t.key, label: t.label }))}
                  error={errors.source_table}
                />
              )}

              {form.metric_type === 'RATIO' ? (
                <>
                  {/* Çapraz tablo toggle */}
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!form.is_cross_table_ratio}
                      onChange={(e) => {
                        const enabling = e.target.checked;
                        setForm((f) => ({
                          ...f,
                          is_cross_table_ratio: enabling,
                          // Çapraz moda geçince kaynak tabloyu her iki leg'e pre-fill et
                          ...(enabling ? {
                            formula_numerator_table: f.source_table || '',
                            formula_denominator_table: f.source_table || '',
                          } : {
                            // Tek tablo moduna dönünce leg alanlarını temizle
                            formula_numerator_table: '',
                            formula_denominator_table: '',
                            numerator_filters: [],
                            numerator_exclude_filters: [],
                            denominator_filters: [],
                            denominator_exclude_filters: [],
                          }),
                        }));
                      }}
                      className="rounded border-neutral-300"
                    />
                    <span className="font-medium text-neutral-700">Çapraz tablo</span>
                    <span className="text-neutral-400 text-xs">— pay ve payda farklı tablolardan geliyorsa işaretle</span>
                  </label>

                  {form.is_cross_table_ratio ? (
                    /* ── ÇAPRAZ TABLO MODU ─────────────────────────────── */
                    <div className="space-y-4">
                      {/* PAY bölümü */}
                      <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-3">
                        <p className="text-sm font-semibold text-blue-800">Pay (Numerator)</p>
                        <div className="grid grid-cols-3 gap-3">
                          <Select
                            label="Tablo"
                            value={form.formula_numerator_table}
                            onChange={(e) => set('formula_numerator_table', e.target.value)}
                            placeholder="Tablo seç"
                            options={chTables.map((t) => ({ value: t.key, label: t.label }))}
                          />
                          <Select
                            label="Hesaplama"
                            value={form.numerator_agg_func || 'SUM'}
                            onChange={(e) => set('numerator_agg_func', e.target.value)}
                            options={[
                              { value: 'SUM', label: 'SUM — Toplam' },
                              { value: 'COUNT', label: 'COUNT — Sayı' },
                              { value: 'AVG', label: 'AVG — Ortalama' },
                            ]}
                          />
                          {form.numerator_agg_func !== 'COUNT' && (
                            <Select
                              label="Kolon"
                              value={form.formula_numerator}
                              onChange={(e) => set('formula_numerator', e.target.value)}
                              placeholder="Kolon seç"
                              options={filterColumnsForMetric(
                                numColumnsForTable,
                                form.numerator_agg_func || 'SUM',
                              ).map((c) => ({ value: c.key, label: decorateColumnLabel(c) }))}
                              error={errors.formula_numerator}
                            />
                          )}
                        </div>
                        {renderPeriodFilterNotice({ table: form.formula_numerator_table, filterKey: 'numerator_filters' })}
                        {renderFilterRowsEditor({
                          title: 'Pay Filtreleri',
                          hint: 'Bu tabloya uygulanacak filtreler',
                          filterKey: 'numerator_filters',
                          columns: numColumnsForTable,
                        })}
                        {renderFilterRowsEditor({
                          title: 'Pay Hariç Tutulacaklar',
                          hint: 'Bu filtreye uyan satırlar pay hesabından düşülür',
                          filterKey: 'numerator_exclude_filters',
                          tone: 'danger',
                          columns: numColumnsForTable,
                        })}
                      </div>

                      {/* PAYDA bölümü */}
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-3">
                        <p className="text-sm font-semibold text-emerald-800">Payda (Denominator)</p>
                        <div className="grid grid-cols-3 gap-3">
                          <Select
                            label="Tablo"
                            value={form.formula_denominator_table}
                            onChange={(e) => set('formula_denominator_table', e.target.value)}
                            placeholder="Tablo seç"
                            options={chTables.map((t) => ({ value: t.key, label: t.label }))}
                          />
                          <Select
                            label="Hesaplama"
                            value={form.denominator_agg_func || 'SUM'}
                            onChange={(e) => set('denominator_agg_func', e.target.value)}
                            options={[
                              { value: 'SUM', label: 'SUM — Toplam' },
                              { value: 'COUNT', label: 'COUNT — Sayı' },
                              { value: 'AVG', label: 'AVG — Ortalama' },
                            ]}
                          />
                          {form.denominator_agg_func !== 'COUNT' && (
                            <Select
                              label="Kolon"
                              value={form.formula_denominator}
                              onChange={(e) => set('formula_denominator', e.target.value)}
                              placeholder="Kolon seç"
                              options={filterColumnsForMetric(
                                denColumnsForTable,
                                form.denominator_agg_func || 'SUM',
                              ).map((c) => ({ value: c.key, label: decorateColumnLabel(c) }))}
                              error={errors.formula_denominator}
                            />
                          )}
                        </div>
                        {renderPeriodFilterNotice({ table: form.formula_denominator_table, filterKey: 'denominator_filters' })}
                        {renderFilterRowsEditor({
                          title: 'Payda Filtreleri',
                          hint: 'Bu tabloya uygulanacak filtreler',
                          filterKey: 'denominator_filters',
                          columns: denColumnsForTable,
                        })}
                        {renderFilterRowsEditor({
                          title: 'Payda Hariç Tutulacaklar',
                          hint: 'Bu filtreye uyan satırlar payda hesabından düşülür',
                          filterKey: 'denominator_exclude_filters',
                          tone: 'danger',
                          columns: denColumnsForTable,
                        })}
                      </div>
                    </div>
                  ) : (
                    /* ── TEK TABLO MODU (mevcut) ───────────────────────── */
                    <div className="grid grid-cols-2 gap-3">
                      <Select
                        label="Pay (Numerator)"
                        value={form.formula_numerator}
                        onChange={(e) => set('formula_numerator', e.target.value)}
                        placeholder="Kolon seç"
                        options={filterColumnsForMetric(columnsForTable, 'SUM')
                          .map((c) => ({ value: c.key, label: decorateColumnLabel(c) }))}
                        error={errors.formula_numerator}
                      />
                      <Select
                        label="Payda (Denominator)"
                        value={form.formula_denominator}
                        onChange={(e) => set('formula_denominator', e.target.value)}
                        placeholder="Kolon seç"
                        options={filterColumnsForMetric(columnsForTable, 'SUM')
                          .map((c) => ({ value: c.key, label: decorateColumnLabel(c) }))}
                        error={errors.formula_denominator}
                      />
                    </div>
                  )}

                  {/* Oran çarpanı — her iki modda da göster */}
                  <div className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-neutral-700 mb-1">
                        Oran Çarpanı
                        <span className="ml-1.5 text-neutral-400 font-normal">(ratio_multiplier)</span>
                      </label>
                      <p className="text-[11px] text-neutral-500">
                        Hesaplanan oran bu sayı ile çarpılır. Yüzde eşik (80/90/100) için <strong>100</strong>; ham oran (0.8/0.9/1.0) için <strong>1</strong>.
                      </p>
                    </div>
                    <Input
                      type="number"
                      min={1}
                      step={1}
                      value={form.ratio_multiplier ?? 100}
                      onChange={(e) => set('ratio_multiplier', Number(e.target.value) || 100)}
                      className="w-24 text-center"
                    />
                  </div>
                </>
              ) : form.metric_type === 'COUNT' ? (
                /* COUNT — kolon gerekmez, satır sayılır.
                   Backend `metric_builder.py` build_aggregate("COUNT", None, None)
                   `count()` döndürür. Kullanıcıya bilgi banner'ı göster ki
                   "Metrik Kolon" alanı seçtirip kafa karıştırmasın. */
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Icon name="info" className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-500" />
                    <div className="space-y-1.5 min-w-0">
                      <p className="font-medium text-blue-900">
                        Adet (COUNT) — otomatik satır sayımı
                      </p>
                      <p className="text-[12px] text-blue-800 leading-relaxed">
                        Bu modda metrik kolonu seçmenize gerek yok. Filtrelere uyan
                        her satır 1 olarak hesaba katılır.
                      </p>
                      <p className="text-[12px] text-blue-700 leading-relaxed">
                        Örn. <code className="rounded bg-blue-100 px-1 py-0.5 font-mono text-[11px]">as_uretim</code>{' '}
                        tablosunda her satır = 1 poliçe. Aşağıdaki <strong>Filtreler</strong> bölümünde
                        durum, branş, yeni/yenileme gibi şartlar ekleyerek hangi
                        poliçelerin sayılacağını belirleyin.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <Select
                  label={`Metrik Kolon (${metricInfo?.label || ''})`}
                  value={form.metric_column}
                  onChange={(e) => set('metric_column', e.target.value)}
                  placeholder={
                    filterColumnsForMetric(columnsForTable, form.metric_type).length === 0
                      ? `Bu tabloda ${metricInfo?.label || form.metric_type} için uygun kolon yok`
                      : 'Kolon seç'
                  }
                  options={filterColumnsForMetric(columnsForTable, form.metric_type)
                    .map((c) => ({ value: c.key, label: decorateColumnLabel(c) }))}
                  error={errors.metric_column}
                />
              )}
            </>
          )}

          {form.metric_type === 'MANUAL' && (
            <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              <Icon name="info" className="inline h-4 w-4 mr-1" />
              Manuel metrik — kullanıcı başına puan admin tarafından girilir. Kaynak tablo gerekmez.
            </div>
          )}
              </div>
            )}
          </div>
          {/* ─ Panel 1 sonu ─ */}

          {/* ═══════════════════════════════════════════
              Panel 2 — Filtreler (yalnızca kaynak tablo seçilince görünür)
          ════════════════════════════════════════════ */}
          {form.metric_type !== 'MANUAL' && form.source_table &&
           !(form.metric_type === 'RATIO' && form.is_cross_table_ratio) && (
            <div className="rounded-xl border border-amber-200 overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 hover:bg-amber-100/70 transition-colors text-left"
                onClick={() => togglePanel('filters')}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-amber-500 text-white">
                    <Icon name="settings" className="h-3 w-3" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-amber-900 leading-tight">Filtreler</p>
                    <p className="text-[11px] text-amber-600 mt-0.5">
                      Dahil etme koşulları · Hariç tutma koşulları
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {(form.filters?.length || 0) + (form.exclude_filters?.length || 0) > 0 && (
                    <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                      {(form.filters?.length || 0) + (form.exclude_filters?.length || 0)} koşul
                    </span>
                  )}
                  <Icon
                    name={openPanels.filters ? 'chevron-up' : 'chevron-down'}
                    className="h-4 w-4 text-amber-500"
                  />
                </div>
              </button>

              {renderPeriodFilterNotice({
                table: form.source_table,
                filterKey: 'filters',
                className: 'border-t border-amber-200 bg-white px-4 pt-3',
              })}

              {openPanels.filters && (
                <div className="border-t border-amber-200 bg-white px-4 py-4 space-y-3">

                  {renderFilterRowsEditor({
                    title: 'Dahil Etme Filtreleri',
                    hint: 'Sayılacak/toplanacak satırları sınırla (örn. durum = YURURLUKTE). Boş = tüm satırlar.',
                    filterKey: 'filters',
                  })}
                  {(form.metric_type === 'COUNT' || form.metric_type === 'SUM' || form.metric_type === 'RATIO') && (
                    renderFilterRowsEditor({
                      title: 'Hariç Tutulacak Filtreler',
                      hint: form.metric_type === 'RATIO'
                          ? 'Bu filtreye uyan satırlar pay ve paydadan birlikte hariç tutulur. Örn. HYO\'da 00-Toplam / 01-Trafik branşları.'
                          : 'Bu filtreye uyan satırların sayısı/toplamı sonuçtan DÜŞÜLÜR. Örn. durum != YURURLUKTE.',
                      filterKey: 'exclude_filters',
                      tone: 'danger',
                    })
                  )}
                </div>
              )}
            </div>
          )}

        </div>
      )}

      {activeTab === 'tiers' && (
        <div className="space-y-3">

          {/* ═══════════════════════════════════════════
              MANUAL: eşik gerekmez bilgi mesajı + Max Puan input
              MANUAL'da eşik tablosu olmadığından max_points'i tier'dan
              türetemiyoruz; admin elle giriyor. Bu değer (a) liderlik
              tablosundaki "% tamamlama" paydasına ve (b) FULL_POINTS
              iptal politikasına etki ediyor — sıfır bırakılmamalı.
          ════════════════════════════════════════════ */}
          {form.metric_type === 'MANUAL' && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4">
                <Icon name="info" className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Manuel görevde eşik tanımlanmaz</p>
                  <p className="text-[12px] text-amber-700 mt-1">
                    Puanlar değerlendirme sonrasında admin panelinden elle girilir.
                    Eşik tablosu ve puanlama modu bu görev türünde kullanılmaz.
                  </p>
                </div>
              </div>
              <div className="rounded-xl border border-neutral-200 bg-white px-4 py-4">
                <Input
                  label="Max Puan (cap)"
                  type="number"
                  value={form.max_points}
                  onChange={(e) => set('max_points', Number(e.target.value))}
                  error={errors.max_points}
                  hint="Bir kullanıcının bu görevden alabileceği en yüksek puan. Liderlik tablosundaki % tamamlama hesabı ve görev iptal edilirse 'Herkese Tam Puan' politikası bu değeri kullanır."
                />
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════
              Puanlama Modu Seçici (RANK ve MANUAL dışında göster)
          ════════════════════════════════════════════ */}
          {form.metric_type !== 'RANK' && form.metric_type !== 'MANUAL' && (
            <div className="rounded-xl border border-neutral-200 bg-white px-4 py-4 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="sliders" className="h-4 w-4 text-neutral-500" />
                <span className="text-sm font-semibold text-neutral-700">Puanlama Modu</span>
              </div>
              <div className="flex gap-3">
                {/* DISCRETE */}
                <label className={
                  'flex-1 flex items-start gap-3 rounded-lg border-2 px-3 py-3 cursor-pointer transition-all ' +
                  (form.tier_mode === 'DISCRETE'
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-neutral-200 hover:border-neutral-300')
                }>
                  <input
                    type="radio"
                    name="tier_mode"
                    value="DISCRETE"
                    checked={form.tier_mode === 'DISCRETE'}
                    onChange={() => set('tier_mode', 'DISCRETE')}
                    className="mt-0.5 accent-indigo-600"
                  />
                  <div>
                    <p className="text-sm font-semibold text-neutral-800">Kademeli</p>
                    <p className="text-[11px] text-neutral-500 mt-0.5">
                      Değer ilk uyan eşiğin sabit puanını alır.
                      Örn: Eşik ≥ 10 → 50 puan, ≥ 15 → 80 puan; değer 13 ise 50 puan.
                    </p>
                  </div>
                </label>
                {/* LINEAR */}
                <label className={
                  'flex-1 flex items-start gap-3 rounded-lg border-2 px-3 py-3 cursor-pointer transition-all ' +
                  (form.tier_mode === 'LINEAR'
                    ? 'border-violet-500 bg-violet-50'
                    : 'border-neutral-200 hover:border-neutral-300')
                }>
                  <input
                    type="radio"
                    name="tier_mode"
                    value="LINEAR"
                    checked={form.tier_mode === 'LINEAR'}
                    onChange={() => set('tier_mode', 'LINEAR')}
                    className="mt-0.5 accent-violet-600"
                  />
                  <div>
                    <p className="text-sm font-semibold text-neutral-800">İnterpolasyonlu</p>
                    <p className="text-[11px] text-neutral-500 mt-0.5">
                      Değer iki eşik arasındaysa puan orantılı hesaplanır.
                      Örn: Eşik 0.70→70 puan, 0.80→80 puan; değer 0.75 ise 75 puan.
                    </p>
                  </div>
                </label>
              </div>
              {form.tier_mode === 'LINEAR' && (
                <div className="flex items-start gap-2 rounded-lg bg-violet-50 border border-violet-200 px-3 py-2">
                  <Icon name="info" className="h-4 w-4 text-violet-500 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-violet-700">
                    <strong>Formül:</strong> Puan = Alt Puan + (Değer − Alt Eşik) ÷ (Üst Eşik − Alt Eşik) × (Üst Puan − Alt Puan)
                    <br />
                    Değer en düşük eşiğin altındaysa 0 puan verilir; en yüksek eşiği geçerse üst kademenin puanı sabit kalır.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════
              Panel 1 — Eşik Kademeleri (indigo) — MANUAL'da gizli
          ════════════════════════════════════════════ */}
          {form.metric_type !== 'MANUAL' && <div className="rounded-xl border border-indigo-200 overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-indigo-50 hover:bg-indigo-100/70 transition-colors text-left"
              onClick={() => togglePanel('tiers')}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-500 text-white">
                  <Icon name="coins" className="h-3 w-3" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-indigo-900 leading-tight">Eşik Kademeleri</p>
                  <p className="text-[11px] text-indigo-600 mt-0.5">
                    {form.metric_type === 'RANK'
                      ? 'Sıraya göre puan · Kademeli mod'
                      : form.tier_mode === 'LINEAR'
                        ? 'İnterpolasyonlu mod · Değer orantılı puan alır'
                        : 'Kademeli mod · İlk uyan eşiğin puanı verilir'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {form.tiers.length > 0 && (
                  <span className="rounded-full bg-indigo-200 px-2 py-0.5 text-[11px] font-medium text-indigo-800">
                    {form.tiers.length} kademe
                  </span>
                )}
                <Icon
                  name={openPanels.tiers ? 'chevron-up' : 'chevron-down'}
                  className="h-4 w-4 text-indigo-500"
                />
              </div>
            </button>

            {openPanels.tiers && (
              <div className="border-t border-indigo-200 bg-white px-4 py-4 space-y-3">
                {(() => {
                  const isLinear = form.tier_mode === 'LINEAR';
                  const thresholdLabel = {
                    COUNT: 'Eşik (Adet)',
                    SUM:   'Eşik (Toplam)',
                    RATIO: 'Eşik (Oran)',
                    RANK:  'Sıra (1 = birinci)',
                    MANUAL:'Eşik',
                  }[form.metric_type] ?? 'Eşik';
                  const gridCols = isLinear
                    ? 'grid-cols-[auto_1fr_1fr_auto]'
                    : 'grid-cols-[auto_1fr_1fr_1fr_auto]';
                  return (
                    <>
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] text-neutral-500">
                          {isLinear
                            ? 'Eşikler küçükten büyüğe sıralanır — kaydetmeden önce otomatik düzenlenir.'
                            : 'Eşik değerleri en yüksekten aşağıya değerlendirilir — ilk uyan kademenin puanı verilir.'}
                        </p>
                        <Button size="sm" variant="secondary" icon="plus" onClick={addTier}>
                          Eşik Ekle
                        </Button>
                      </div>

                      {/* Kolon başlıkları */}
                      {form.tiers.length > 0 && (
                        <div className={`grid ${gridCols} gap-2 px-2`}>
                          <div className="w-8" />
                          {!isLinear && (
                            <div className="text-[10px] font-semibold text-neutral-500 text-center uppercase tracking-wide">Operatör</div>
                          )}
                          <div className="text-[10px] font-semibold text-neutral-500 text-center uppercase tracking-wide">{thresholdLabel}</div>
                          <div className="text-[10px] font-semibold text-neutral-500 text-center uppercase tracking-wide">Puan</div>
                          <div />
                        </div>
                      )}

                      <div className="space-y-2">
                        {form.tiers.map((t, i) => (
                          <div
                            key={i}
                            className={`grid ${gridCols} items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 p-2`}
                          >
                            <Badge color="blue">#{i + 1}</Badge>
                            {!isLinear && (
                              <Select
                                value={t.threshold_operator}
                                onChange={(e) => updateTier(i, { threshold_operator: e.target.value })}
                                options={[
                                  { value: 'GTE', label: '≥ (büyük eşit)' },
                                  { value: 'GT', label: '> (büyük)' },
                                  { value: 'EQ', label: '= (eşit)' },
                                  { value: 'LT', label: '< (küçük)' },
                                  { value: 'LTE', label: '≤ (küçük eşit)' },
                                ]}
                              />
                            )}
                            <Input
                              type="number"
                              placeholder="0"
                              value={t.threshold_value}
                              onChange={(e) => updateTier(i, { threshold_value: Number(e.target.value) })}
                            />
                            <Input
                              type="number"
                              placeholder="0"
                              value={t.points}
                              onChange={(e) => updateTier(i, { points: Number(e.target.value) })}
                            />
                            <button
                              type="button"
                              onClick={() => removeTier(i)}
                              className="p-1.5 text-neutral-400 hover:text-danger-500"
                            >
                              <Icon name="trash" className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}


                {form.tiers.length === 0 && (
                  <div className="rounded-lg border border-dashed border-neutral-200 bg-neutral-50 p-3 text-center">
                    <p className="text-xs text-neutral-400">Henüz eşik tanımlanmadı. Eşik Ekle ile başlayın.</p>
                  </div>
                )}
              </div>
            )}
          </div>}

          {/* ═══════════════════════════════════════════
              Panel 2 — Bonus Koşulu (emerald)

              NOT: MANUAL metric tipinde gizli — bonus koşulu
              ClickHouse metric verisine karşı çalışır; MANUAL'da
              kaynak veri yok, admin elle puan giriyor. Eşik
              Kademeleri paneli de aynı sebeple MANUAL'da gizli.
          ════════════════════════════════════════════ */}
          {form.metric_type !== 'MANUAL' && <div className="rounded-xl border border-emerald-200 overflow-hidden">
            <button
              type="button"
              className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-emerald-50 hover:bg-emerald-100/70 transition-colors text-left"
              onClick={() => togglePanel('bonus')}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                  <Icon name="zap" className="h-3 w-3" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-emerald-900 leading-tight">
                    Bonus Koşulu
                    <span className="ml-2 text-[11px] font-normal text-emerald-600">(opsiyonel)</span>
                  </p>
                  <p className="text-[11px] text-emerald-600 mt-0.5">
                    Örn. DASK satışı varsa +X puan · kademeli puanlamaya ek
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {form.bonus_condition && (
                  <span className="rounded-full bg-emerald-200 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
                    aktif · +{form.bonus_condition.bonus_points ?? 0} puan
                  </span>
                )}
                <Icon
                  name={openPanels.bonus ? 'chevron-up' : 'chevron-down'}
                  className="h-4 w-4 text-emerald-500"
                />
              </div>
            </button>

            {openPanels.bonus && (
              <div className="border-t border-emerald-200 bg-white px-4 py-4 space-y-3">
                <div className="flex items-center gap-2 text-xs text-neutral-600">
                  <input
                    type="checkbox"
                    checked={!!form.bonus_condition}
                    onChange={(e) =>
                      set(
                        'bonus_condition',
                        e.target.checked ? BONUS_SIMPLE_INITIAL : null,
                      )
                    }
                    className="rounded border-neutral-300"
                  />
                  <span>Bu görev için bonus koşulu tanımla</span>
                </div>

                {form.bonus_condition && (
                  <div className="space-y-3">

                    {/* ── Bonus Tipi Seçici ─────────────────────────────── */}
                    <Select
                      label="Bonus Tipi"
                      value={bonusType}
                      onChange={(e) => {
                        const t = e.target.value;
                        set('bonus_condition',
                          t === 'CROSS_BRANCH_RATIO_THRESHOLD'
                            ? { ...BONUS_RATIO_INITIAL }
                            : { ...BONUS_SIMPLE_INITIAL },
                        );
                      }}
                      options={BONUS_TYPES.map((bt) => ({ value: bt.value, label: bt.label }))}
                    />

                    {/* ── SIMPLE form — çoklu filtre ────────────────────── */}
                    {bonusType === 'SIMPLE' && (() => {
                      const incFilters = form.bonus_condition.filters || [];
                      const excFilters = form.bonus_condition.exclude_filters || [];

                      const renderBonusFilterRow = ({ leg, idx, row, tone }) => (
                        <div className="flex items-end gap-2">
                          <div className="flex-1 grid grid-cols-3 gap-2">
                            <Select
                              label={idx === 0 ? 'Kolon' : undefined}
                              value={row.column || ''}
                              onChange={(e) => updateBonusFilter(leg, idx, { column: e.target.value })}
                              placeholder="Kolon"
                              options={columnsForTable.map((c) => ({ value: c.key, label: c.label }))}
                            />
                            <Select
                              label={idx === 0 ? 'Operatör' : undefined}
                              value={row.op || '='}
                              onChange={(e) => updateBonusFilter(leg, idx, { op: e.target.value })}
                              options={BONUS_OPS.map((o) => ({ value: o.value, label: o.label }))}
                            />
                            <div>
                              {idx === 0 && <label className="block text-xs font-medium text-neutral-700 mb-1">Değer</label>}
                              <div className="flex items-center gap-1">
                                <input
                                  className="flex-1 min-w-0 rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  value={row.value ?? ''}
                                  onChange={(e) => updateBonusFilter(leg, idx, { value: e.target.value })}
                                  placeholder="örn. DASK"
                                />
                                <button
                                  type="button"
                                  title="CH'den değer seç"
                                  disabled={!row.column || !form.source_table}
                                  onClick={() => setValuePicker({
                                    bonusLeg: leg, bonusIdx: idx,
                                    table: form.source_table,
                                    column: row.column,
                                    op: row.op,
                                    current: row.value,
                                  })}
                                  className="p-1.5 text-success-600 hover:text-success-800 hover:bg-success-50 rounded-md transition-colors disabled:opacity-30"
                                >
                                  <Icon name="search" className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  title="Parametreden seç"
                                  onClick={() => setConstPicker({ bonusLeg: leg, bonusIdx: idx })}
                                  className="p-1.5 text-primary-500 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                                >
                                  <Icon name="settings" className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                          {(leg === 'filters' ? incFilters : excFilters).length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeBonusFilter(leg, idx)}
                              className="mb-0.5 p-1 text-neutral-400 hover:text-red-500 rounded"
                            >
                              <Icon name="x" className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      );

                      return (
                        <div className="space-y-3">
                          {/* Dahil Et */}
                          <div className="rounded-md border border-emerald-200 p-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-semibold text-emerald-700">✓ Dahil Et</p>
                              <button
                                type="button"
                                onClick={() => addBonusFilter('filters')}
                                className="text-[11px] text-emerald-600 hover:text-emerald-800 flex items-center gap-0.5"
                              >
                                <Icon name="plus" className="h-3 w-3" /> Koşul ekle
                              </button>
                            </div>
                            {incFilters.map((row, idx) => (
                              <Fragment key={idx}>{renderBonusFilterRow({ leg: 'filters', idx, row, tone: 'emerald' })}</Fragment>
                            ))}
                            {incFilters.length === 0 && (
                              <p className="text-xs text-neutral-400 italic">Koşul yok — tüm kullanıcılar dahil</p>
                            )}
                          </div>

                          {/* Dahil Etme */}
                          <div className="rounded-md border border-red-200 p-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-semibold text-red-600">✗ Dahil Etme</p>
                              <button
                                type="button"
                                onClick={() => addBonusFilter('exclude_filters')}
                                className="text-[11px] text-red-500 hover:text-red-700 flex items-center gap-0.5"
                              >
                                <Icon name="plus" className="h-3 w-3" /> Koşul ekle
                              </button>
                            </div>
                            {excFilters.map((row, idx) => (
                              <Fragment key={idx}>{renderBonusFilterRow({ leg: 'exclude_filters', idx, row, tone: 'red' })}</Fragment>
                            ))}
                            {excFilters.length === 0 && (
                              <p className="text-xs text-neutral-400 italic">Çıkarma koşulu yok</p>
                            )}
                          </div>

                          {/* Bonus Puan */}
                          <Input
                            label="Bonus Puan"
                            type="number"
                            min={0}
                            value={form.bonus_condition.bonus_points ?? 0}
                            onChange={(e) =>
                              set('bonus_condition', {
                                ...form.bonus_condition,
                                bonus_points: Number(e.target.value),
                              })
                            }
                          />
                        </div>
                      );
                    })()}

                    {/* ── CROSS_BRANCH_RATIO_THRESHOLD form ─────────────── */}
                    {bonusType === 'CROSS_BRANCH_RATIO_THRESHOLD' && (() => {
                      const tgtFilters = form.bonus_condition.target_filters || [];

                      const renderCrossFilterRow = ({ idx, row }) => (
                        <div className="flex items-end gap-2">
                          <div className="flex-1 grid grid-cols-3 gap-2">
                            <Select
                              label={idx === 0 ? 'Kolon' : undefined}
                              value={row.column || ''}
                              onChange={(e) => updateBonusFilter('target_filters', idx, { column: e.target.value })}
                              placeholder="Kolon"
                              options={columnsForTable.map((c) => ({ value: c.key, label: c.label }))}
                            />
                            <Select
                              label={idx === 0 ? 'Operatör' : undefined}
                              value={row.op || '='}
                              onChange={(e) => updateBonusFilter('target_filters', idx, { op: e.target.value })}
                              options={BONUS_OPS.map((o) => ({ value: o.value, label: o.label }))}
                            />
                            <div>
                              {idx === 0 && <label className="block text-xs font-medium text-neutral-700 mb-1">Değer</label>}
                              <div className="flex items-center gap-1">
                                <input
                                  className="flex-1 min-w-0 rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  value={row.value ?? ''}
                                  onChange={(e) => updateBonusFilter('target_filters', idx, { value: e.target.value })}
                                  placeholder="örn. KONUT"
                                />
                                <button
                                  type="button"
                                  title="CH'den değer seç"
                                  disabled={!row.column || !form.source_table}
                                  onClick={() => setValuePicker({
                                    bonusLeg: 'target_filters', bonusIdx: idx,
                                    table: form.source_table,
                                    column: row.column,
                                    op: row.op,
                                    current: row.value,
                                  })}
                                  className="p-1.5 text-success-600 hover:text-success-800 hover:bg-success-50 rounded-md transition-colors disabled:opacity-30"
                                >
                                  <Icon name="search" className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  title="Parametreden seç"
                                  onClick={() => setConstPicker({ bonusLeg: 'target_filters', bonusIdx: idx })}
                                  className="p-1.5 text-primary-500 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                                >
                                  <Icon name="settings" className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                          {tgtFilters.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeBonusFilter('target_filters', idx)}
                              className="mb-0.5 p-1 text-neutral-400 hover:text-red-500 rounded"
                            >
                              <Icon name="x" className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      );

                      // Görevin kendi metriğini özetleyen etiket
                      const srcTable = form.source_table || '—';
                      const srcMetric = form.metric_type || 'COUNT';
                      const srcFilters = Array.isArray(form.filters) ? form.filters.filter((f) => f.column) : [];
                      const srcFilterStr = srcFilters.length
                        ? srcFilters.map((f) => `${f.column} ${f.op} ${f.value ?? ''}`).join(', ')
                        : 'filtre yok';
                      const srcLabel = `${srcMetric}(${srcTable} | ${srcFilterStr})`;

                      return (
                        <div className="space-y-3">
                          <div className="text-xs text-neutral-500 bg-neutral-50 rounded-md px-3 py-2 space-y-1.5">
                            <p>
                              <strong>Formül:</strong>{' '}
                              <span className="font-mono text-emerald-700 bg-emerald-50 px-1 rounded">KAYNAK</span>
                              {' '}≥{' '}
                              <span className="font-mono text-blue-700 bg-blue-50 px-1 rounded">HEDEF</span>
                              {' '}× çarpan → bonus puan
                            </p>
                            <p>
                              <span className="font-mono text-emerald-700 bg-emerald-50 px-1 rounded">KAYNAK</span>
                              {' '}={' '}
                              <span className="font-mono text-[11px] text-emerald-800">{srcLabel}</span>
                            </p>
                            <p>
                              <span className="font-mono text-blue-700 bg-blue-50 px-1 rounded">HEDEF</span>
                              {' '}= aşağıda tanımlanan hedef branş filtresi sayısı
                            </p>
                          </div>

                          {/* Hedef Branş */}
                          <div className="rounded-md border border-blue-200 p-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-semibold text-blue-700">Hedef Branş Filtresi (ör. KONUT)</p>
                              <button
                                type="button"
                                onClick={() => addBonusFilter('target_filters')}
                                className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
                              >
                                <Icon name="plus" className="h-3 w-3" /> Koşul ekle
                              </button>
                            </div>
                            {tgtFilters.map((row, idx) => (
                              <Fragment key={idx}>{renderCrossFilterRow({ idx, row })}</Fragment>
                            ))}
                          </div>

                          {/* Çarpan + Bonus Puan */}
                          <div className="grid grid-cols-2 gap-3">
                            <Input
                              label="Çarpan"
                              type="number"
                              min={0.01}
                              max={99}
                              step={0.05}
                              value={form.bonus_condition.ratio_threshold ?? 0.5}
                              onChange={(e) =>
                                set('bonus_condition', {
                                  ...form.bonus_condition,
                                  ratio_threshold: Number(e.target.value),
                                })
                              }
                              hint="0.5 = %50 · 1.0 = eşit · 2.0 = %200"
                            />
                            <Input
                              label="Bonus Puan"
                              type="number"
                              min={0}
                              value={form.bonus_condition.bonus_points ?? 0}
                              onChange={(e) =>
                                set('bonus_condition', {
                                  ...form.bonus_condition,
                                  bonus_points: Number(e.target.value),
                                })
                              }
                            />
                          </div>
                        </div>
                      );
                    })()}

                  </div>
                )}
              </div>
            )}
          </div>}

        </div>
      )}
    </Modal>

    {/*
      Sistem sabiti picker — filter satirindaki "⚙" butonuna tiklanildiginda
      acilir. Secim yapildiginda ilgili filtrenin value'suna "${KEY}"
      referansi yazilir; evaluation worker bu referansi runtime'da sistem
      sabiti degeriyle degistirir.
    */}
    <SystemConstantPicker
      open={!!constPicker}
      onClose={() => setConstPicker(null)}
      onPick={(paramRef) => {
        if (!constPicker) return;
        if (constPicker.bonusLeg != null) {
          updateBonusFilter(constPicker.bonusLeg, constPicker.bonusIdx, { value: paramRef });
        } else {
          updateFilter(constPicker.filterKey, constPicker.index, { value: paramRef });
        }
        setConstPicker(null);
      }}
    />

    <ColumnValuePicker
      open={!!valuePicker}
      onClose={() => setValuePicker(null)}
      table={valuePicker?.table}
      column={valuePicker?.column}
      op={valuePicker?.op}
      current={valuePicker?.current}
      onPick={(value) => {
        if (!valuePicker) return;
        if (valuePicker.bonusLeg != null) {
          updateBonusFilter(valuePicker.bonusLeg, valuePicker.bonusIdx, { value });
        } else {
          updateFilter(valuePicker.filterKey, valuePicker.index, { value });
        }
        setValuePicker(null);
      }}
    />

    {/* Metrik tipi info tooltip — fixed positioned, modal overflow'undan etkilenmez */}
    {tooltipMetric && (() => {
      const desc = METRIC_DESCRIPTIONS[tooltipMetric];
      if (!desc) return null;
      return (
        <>
          {/* Backdrop: dışarı tıklayınca kapat — stopPropagation ile modal backdrop'a sızmasın */}
          <div
            className="fixed inset-0 z-[9998]"
            onClick={(e) => { e.stopPropagation(); setTooltipMetric(null); }}
          />
          <div
            className="fixed z-[9999] w-72 rounded-xl border border-neutral-200 bg-white p-3 shadow-xl"
            style={{ top: tooltipPos.top, left: tooltipPos.left }}
          >
            <p className="text-xs font-semibold text-primary-700 mb-1">
              {desc.summary}
            </p>
            <p className="text-[11px] text-neutral-600 mb-2 leading-relaxed">
              {desc.detail}
            </p>
            <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wide mb-1">
              Örnek senaryolar
            </p>
            <ul className="space-y-1">
              {desc.examples.map((ex, ei) => (
                <li key={ei} className="flex gap-1.5 text-[11px] text-neutral-700">
                  <span className="text-primary-400 flex-shrink-0 mt-0.5">›</span>
                  <span>{ex}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      );
    })()}
    </>
  );
}
